import type { TranslationKey } from "../index";

export const es: Record<TranslationKey, string> = {
    // --- common — short words/phrases reused verbatim across unrelated components ------------------
    "common.brandHomeAriaLabel": "SoundCheckBox — inicio",
    "common.close": "Cerrar",
    "catalogCard.open": "Ficha del catálogo",
    "catalogCard.openAlternatives": "Fichas de las alternativas",
    "catalogCard.alternatives": "Alternativas",
    "catalogCard.type": "Tipo",
    "catalogCard.category": "Categoría",
    "catalogCard.weight": "Peso",
    "catalogCard.power": "Potencia",
    "about.heading": "Acerca de esta compilación",
    "about.web": "Web",
    "about.api": "API",
    "about.schema": "Contrato del esquema",
    "about.unknown": "desconocido",
    "about.unstamped": "compilación sin sello",
    "about.envProduction": "Producción",
    "about.envNotProduction": "{env} — no es producción",
    "about.mismatch": "El paquete web y la API se compilaron desde commits distintos. El despliegue llegó a medias: lo que ve y lo que responde no son el mismo código.",
    "common.cancel": "Cancelar",
    "common.delete": "Eliminar",
    "ridersList.deletePublished.heading": "Este rider está publicado",
    "ridersList.deletePublished.body": "Al eliminarlo también se desactiva su enlace público. Nadie que lo tenga — una sala, una empresa de alquiler, quien escaneó el QR impreso — podrá volver a abrirlo, y no se puede restaurar.",
    "ridersList.deletePublished.dontAskAgain": "No preguntar de nuevo en esta sesión",
    "common.deletePermanentlyPrompt": "¿Eliminar permanentemente?",
    "common.yesDelete": "Sí, eliminar",
    "common.loading": "Cargando…",
    "common.saving": "Guardando…",
    "common.sending": "Enviando…",

    // --- side-nav (components/side-nav.ts) ----------------------------------------------------------
    "sideNav.home": "Inicio",
    "sideNav.soundman": "Técnico de sonido",
    "sideNav.soundmanComingSoon": "Técnico de sonido — próximamente",
    "sideNav.myRiders": "Mis Riders",
    "sideNav.offers": "Ofertas",
    "myEquipment.warehouse.heading": "Mi almacén",
    "myEquipment.warehouse.search": "Buscar en mi almacén",
    "myEquipment.warehouse.add": "Añadir a mi almacén",
    "myEquipment.warehouse.formed": "Mi almacén está completo — las ofertas sugieren primero de él",
    "myEquipment.backToOffer": "← Volver a la oferta",
    "myEquipment.fromOffer.line": "De {offer} para el rider {rider}, añadido a su almacén: {items}.",
    "myEquipment.fromOffer.grow": "Amplíe su almacén:",
    "myEquipment.fromOffer.pick": "Elegir de nuestro catálogo",
    "myEquipment.fromOffer.import": "Importar su lista",
    "myEquipment.fromOffer.dismiss": "Ocultar",
    "myEquipment.weight.notSpecified": "No especificado",
    "myEquipment.col.manufacturer": "Fabricante",
    "myEquipment.sort.by": "Ordenar por",
    "myEquipment.sort.category": "Categoría",
    "myEquipment.more": "Más",
    "myEquipment.searchClose": "Cerrar búsqueda",
    "sideNav.inbox": "Bandeja de entrada",
    "sideNav.inboxComingSoon": "Bandeja de entrada — próximamente",
    "sideNav.outbox": "Bandeja de salida",
    "sideNav.outboxComingSoon": "Bandeja de salida — próximamente",
    "sideNav.eventManager": "Organizador de eventos",
    "sideNav.eventManagerComingSoon": "Organizador de eventos — próximamente",
    "sideNav.warehouse": "Almacén",
    "sideNav.warehouseComingSoon": "Almacén — próximamente",
    "sideNav.myProfile": "Mi perfil",
    "sideNav.admin": "Administración",
    "sideNav.designThemeLabel": "Tema de diseño",
    "sideNav.languageLabel": "Idioma",
    "sideNav.mainAriaLabel": "Menú principal",

    // --- app-header (components/app-header.ts) ------------------------------------------------------
    "appHeader.adminTitle": "Administrador",
    "appHeader.adminBadge": "admin",
    "appHeader.logoutTitle": "Cerrar sesión",
    "appHeader.logoutLabel": "Salir",

    // --- verify-email banner (components/verify-email-banner.ts) -------------------------------------
    "verifyBanner.message": "Tu correo aún no está confirmado — guardar, publicar y otros cambios están deshabilitados hasta que lo verifiques.",
    "verifyBanner.profileLink": "Ir al perfil",
    "verifyBanner.resend": "Reenviar correo de confirmación",
    "verifyBanner.resendSent": "Enviado ✓",
    "verifyBanner.resendFailed": "No se pudo reenviar el correo de confirmación — inténtalo de nuevo desde tu perfil.",
    "verifyBanner.dismissAriaLabel": "Descartar",

    // --- write errors shown in place of a raw API error (api/http.ts#isEmailNotVerifiedError) --------
    "errors.emailNotVerified": "Verifica tu correo antes de hacer cambios — ve a tu perfil para reenviar el enlace.",

    // --- login (pages/login-page.ts) -----------------------------------------------------------------
    "login.hint": "Inicia sesión o crea una cuenta para crear y guardar riders técnicos.",
    "login.hintRegister": "Crea una cuenta — basta un correo y una contraseña, y tu trabajo te acompaña.",
    "login.emailLabel": "Correo o usuario",
    "login.passwordLabel": "Contraseña",
    "login.submit": "Iniciar sesión",
    "login.register": "Registrarse",
    "login.consentNotice": "Al crear una cuenta aceptas nuestros {termsLink} y nuestra {privacyLink}.",
    "login.termsLink": "Términos de uso",
    "login.privacyLink": "Política de privacidad",
    "legal.privacyTitle": "Política de privacidad",
    "legal.termsTitle": "Términos de uso",
    "legal.unavailable": "No se pudo cargar este documento. Inténtalo de nuevo más tarde.",
    "consent.title": "Revisa nuestras condiciones",
    "consent.body": "Para seguir usando SoundCheckBox, acepta nuestros {termsLink} y nuestra {privacyLink}.",
    "consent.changeNote": "Podemos actualizar estos documentos. Si no estás de acuerdo con un cambio, puedes dejar de usar el servicio.",
    "consent.agree": "Acepto",
    "consent.decline": "Cerrar sesión",
    "consent.saveFailed": "No pudimos registrar tu aceptación. Inténtalo de nuevo.",
    "editor.publishConfirm": "Este rider se publicará en un enlace público que cualquiera puede abrir, sin necesidad de cuenta, y seguirá disponible hasta que lo despubliques. Todo lo que contenga, incluidos nombres, teléfonos y correos, también será público. ¿Publicarlo?",
    "editor.publishImpact.offer": "Este rider está en una oferta aún no acordada ({shows}). Publicar una versión nueva cambiará esa oferta: el proveedor volverá a responder las líneas modificadas. ¿Publicar?",
    "editor.publishImpact.contract": "Este rider está bajo una oferta acordada ({shows}). Allí no se puede cambiar sin cambiar el contrato del show: el proveedor deberá confirmar la diferencia y el equipo verá la nueva versión. ¿Publicar?",
    "editor.publishImpact.unnamedShow": "un show sin nombre",
    "editor.unpublishConfirm": "Despublicar anula TODOS los enlaces públicos de este rider —esta versión y todas las anteriores— y quien ya tenga un enlace dejará de poder abrirlo. No se puede deshacer: volver a publicar crea un enlace NUEVO. ¿Despublicarlo?",
    "editor.publishDirty.heading": "¿Publicar cambios sin guardar?",
    "editor.publishDirty.body": "Se publica la última versión guardada de este rider, y tus últimos cambios aún no están guardados. Guárdalos primero para publicarlos.",
    "editor.publishDirty.saveAndPublish": "Guardar y publicar",
    "login.minAge": "Debes tener al menos 16 años para crear una cuenta.",
    "admin.title": "Consola de administración",
    "admin.notAvailable": "Esta página solo está disponible para administradores.",
    "admin.users.title": "Usuarios",
    "admin.users.searchPlaceholder": "Buscar por email o usuario",
    "admin.users.searchBtn": "Buscar",
    "admin.users.showAdmins": "Mostrar administradores",
    "admin.users.browseAll": "Ver todos",
    "admin.users.prevPage": "Anterior",
    "admin.users.nextPage": "Siguiente",
    "admin.users.pageOf": "Página {page} de {pages} — {total} cuentas",
    "admin.users.searchTooShort": "Introduce al menos 2 caracteres.",
    "admin.users.noMatch": "Sin coincidencias.",
    "admin.users.colEmail": "Email",
    "admin.users.colLogin": "Usuario",
    "admin.users.colJoined": "Alta",
    "admin.users.colVerified": "Verificado",
    "admin.users.colAdmin": "Admin",
    "admin.users.colActions": "Acciones",
    "admin.users.verify": "Marcar verificado",
    "admin.users.grantAdmin": "Hacer admin",
    "admin.users.revokeAdmin": "Quitar admin",
    "admin.users.verified": "Email marcado como verificado.",
    "admin.users.granted": "Acceso de administrador concedido.",
    "admin.users.revoked": "Acceso de administrador retirado.",
    "admin.users.confirmGrant": "¿Conceder acceso de administrador a {user}?",
    "admin.users.confirmRevoke": "¿Retirar el acceso de administrador a {user}?",
    "admin.users.deletionPending": "eliminación solicitada",
    "admin.users.tempPassword": "Contraseña temporal",
    "admin.users.tempPasswordPending": "contraseña temporal",
    "admin.users.confirmTempPassword": "¿Emitir una contraseña temporal para {user}? Su contraseña actual dejará de funcionar de inmediato.",
    "admin.users.tempPasswordIssued": "Contraseña temporal de {user}. Cópiala ahora: no se puede volver a mostrar:",
    "admin.users.tempPasswordDone": "Contraseña temporal emitida.",
    // --- users tab: sort, filters, summary (SCRUM-570) ---
    "admin.users.colLastLogin": "Último acceso",
    "admin.users.colRiders": "Riders",
    "admin.users.colLastRiderEdit": "Última edición de rider",
    "admin.users.sortBy": "Ordenar por {column}",
    "admin.users.noLoginRecorded": "Sin registro. Los accesos solo se guardan desde octubre de 2026; antes no se almacenaban, así que el guion no significa que la cuenta no se haya usado.",
    "admin.users.noRiderEdit": "La cuenta no tiene riders, por lo que no hay ninguna edición.",
    "admin.users.filtersTitle": "Filtros",
    "admin.users.filterAll": "Todos",
    "admin.users.filterRole": "Rol",
    "admin.users.filterRoleUsers": "Usuarios",
    "admin.users.filterRoleAdmins": "Administradores",
    "admin.users.filterVerified": "Email verificado",
    "admin.users.filterVerifiedYes": "Verificado",
    "admin.users.filterVerifiedNo": "Sin verificar",
    "admin.users.filterRiders": "Riders",
    "admin.users.filterRidersWith": "Con riders",
    "admin.users.filterRidersWithout": "Sin riders",
    "admin.users.filterActiveDays": "Editó un rider en los últimos … días",
    "admin.users.filterJoinedDays": "Se registró en los últimos … días",
    "admin.users.filterAnyDays": "cualquiera",
    "admin.users.clearFilters": "Quitar filtros",
    "admin.stats.title": "Resumen",
    "admin.stats.filteredHint": "Las cifras cubren las cuentas que cumplen los filtros de arriba.",
    "admin.stats.unfilteredHint": "Las cifras cubren todas las cuentas.",
    "admin.stats.total": "Cuentas",
    "admin.stats.admins": "Administradores",
    "admin.stats.verified": "Verificadas",
    "admin.stats.withRiders": "Con riders",
    "admin.stats.ridersTotal": "Riders en total",
    "admin.stats.windowHeader": "En los últimos…",
    "admin.stats.days7": "7 días",
    "admin.stats.days30": "30 días",
    "admin.stats.days90": "90 días",
    "admin.stats.days365": "365 días",
    "admin.stats.activeRiderAuthors": "Editaron un rider",
    "admin.stats.newUsers": "Se registraron",
    "admin.stats.loggedIn": "Accedieron",
    "admin.stats.loginNote": "Los accesos se registran solo desde octubre de 2026. La fila «Accedieron» cuenta desde ese día; las sesiones anteriores no están incluidas.",
    "admin.erasure.title": "Cuentas pendientes de borrado",
    "admin.erasure.hint": "Solicitudes de eliminación cuyo plazo de espera ha vencido. No se borra nada automáticamente: el borrado es irreversible y elimina la cuenta con todos sus riders.",
    "admin.erasure.none": "No hay nada pendiente.",
    "admin.erasure.colRequested": "Solicitado",
    "admin.erasure.colDue": "Vencido desde",
    "admin.erasure.erase": "Borrar",
    "admin.erasure.confirmPrompt": "Escribe {user} para borrar esta cuenta y todos sus riders. No se puede deshacer.",
    "admin.erasure.confirmMismatch": "La dirección no coincide: no se ha borrado nada.",
    "admin.erasure.erased": "{user} ha sido borrada.",
    "admin.content.legalTitle": "Documentos legales",
    "admin.content.legalHint": "Sustituye al documento incluido con el sitio. Vacío significa que se usa el archivo del repositorio, igual que antes. Markdown.",
    "admin.content.mailTitle": "Textos de los correos",
    "admin.content.mailHint": "Sustituye cadenas concretas de los correos, por idioma. El campo contiene el texto vigente: edítalo ahí mismo. Guardar sin cambios mantiene la versión incluida; «Restaurar» descarta tu edición. Los marcadores entre llaves deben conservarse tal cual: se rellenan al enviar.",
    "admin.content.localeLabel": "Idioma",
    "admin.content.usingFile": "versión incluida",
    "admin.content.edited": "editado aquí",
    "admin.content.legalPlaceholder": "Vacío: el sitio muestra el documento del repositorio.",
    "admin.content.colKey": "Clave",
    "admin.content.colText": "Texto",
    "admin.content.save": "Guardar",
    "admin.content.revert": "Restaurar",
    "admin.content.saved": "Guardado.",
    "admin.content.reverted": "Restaurada la versión incluida.",
    "admin.content.confirmRevert": "¿Descartar esta edición y volver al documento incluido?",
    "admin.content.uiTitle": "Textos de la interfaz",
    "admin.content.uiHint": "Sustituye cadenas concretas de la interfaz, por idioma, sin recompilar. El campo contiene el texto vigente: edítalo ahí mismo. Guardar sin cambios mantiene la versión incluida; «Restaurar» descarta tu edición. Los cambios se ven tras recargar la página.",
    "admin.content.uiSearchPlaceholder": "Buscar por clave o texto",
    "admin.content.uiEditedOnly": "solo editadas",
    "admin.content.uiStart": "Escribe algo para buscar o marca «solo editadas».",
    "admin.content.uiTooMany": "{count} coincidencias: se muestran las primeras 200. Afina la búsqueda.",
    "admin.content.badPlaceholders": "Marcadores desconocidos: {list}. Se imprimirían tal cual.",
    "admin.content.savedReload": "Guardado. Recarga la página para verlo.",
    "admin.tabs.users": "Cuentas",
    "admin.tabs.content": "Textos",
    "admin.tabs.settings": "Ajustes",
    "admin.content.history": "Historial",
    "admin.content.noHistory": "Aún no hay versiones anteriores.",
    "admin.content.restore": "Cargar esta versión",
    "admin.content.restoredIntoEditor": "Cargada en el editor: revísala y guarda.",
    "admin.settings.title": "Ajustes del sitio",
    "admin.settings.hint": "Los cambios se aplican de inmediato. Un ajuste sin anulación usa el valor que trae el código.",
    "admin.settings.save": "Guardar",
    "admin.settings.reset": "Restablecer",
    "admin.settings.saved": "Guardado: {key}.",
    "admin.settings.wasReset": "{key} vuelve a su valor por defecto.",
    "admin.settings.overridden": "modificado (por defecto: {default})",
    "admin.settings.isDefault": "por defecto",
  "admin.models.title": "Modelos 3D sin usar",
  "admin.models.hint": "Archivos .glb subidos a los que no apunta ningún preset ni ningún rider guardado. Borrarlos es permanente: no hay historial de subidas para restaurar.",
  "admin.models.scan": "Analizar",
  "admin.models.scanning": "Analizando…",
  "admin.models.none": "Nada que limpiar: todos los modelos subidos están en uso.",
  "admin.models.summary": "{count} sin usar, {size} en total. {inUse} todavía en uso. Se revisaron {presets} presets y {riders} riders.",
  "admin.models.colFile": "Archivo",
  "admin.models.colSize": "Tamaño",
  "admin.models.colModified": "Subido",
  "admin.models.deleteSelected": "Borrar seleccionados",
  "admin.models.deleteSelectedCount": "Borrar seleccionados ({count})",
  "admin.models.confirmDelete": "¿Borrar permanentemente {count} archivo(s) de modelo? No se puede deshacer.",
  "admin.models.deleted": "Se borraron {count} archivo(s), se liberaron {size}.",
  "admin.models.skipped": "{count} omitidos: ahora están referenciados.",
    "login.forgotPassword": "¿Olvidaste tu contraseña?",
    "login.sessionExpired": "Tu sesión ha caducado — vuelve a iniciar sesión.",
    "login.invalidCredentials": "Correo/usuario o contraseña incorrectos.",

    // --- recovery (pages/recovery-pages.ts) -----------------------------------------------------------
    "recovery.forgotTitle": "Restablece tu contraseña",
    "recovery.forcedTitle": "Elige tu propia contraseña",
    "recovery.forcedLead": "Has entrado con una contraseña emitida por soporte. Define la tuya para continuar; hasta entonces no hay nada más disponible.",
    "recovery.forcedCurrentLabel": "Contraseña temporal",
    "recovery.forcedSubmit": "Guardar contraseña",
    "recovery.forgotLead": "Ingresa tu correo electrónico y te enviaremos un enlace para elegir una nueva contraseña.",
    "recovery.emailLabel": "Correo electrónico",
    "recovery.forgotSubmit": "Enviar enlace de restablecimiento",
    "recovery.backToSignIn": "Volver a iniciar sesión",
    "recovery.forgotSuccess": "Si la dirección existe en nuestro sistema, te hemos enviado un enlace. Revisa tu bandeja de entrada y spam.",
    "recovery.resetIncompleteTitle": "Enlace de restablecimiento incompleto",
    "recovery.resetIncompleteLead": "A este enlace le falta el token de verificación. Solicita uno nuevo.",
    "recovery.resetAgainLink": "Solicitar un nuevo enlace",
    "recovery.resetTitle": "Elige una nueva contraseña",
    "recovery.newPasswordLabel": "Nueva contraseña",
    "recovery.repeatPasswordLabel": "Repite la nueva contraseña",
    "recovery.resetSubmit": "Establecer nueva contraseña",
    "recovery.passwordMismatch": "Las contraseñas no coinciden.",
    "recovery.resetSuccess": "Contraseña actualizada. Redirigiéndote al inicio de sesión…",
    "recovery.verifyTitle": "Confirmando tu correo electrónico",
    "recovery.verifyChecking": "Verificando tu enlace…",
    "recovery.continueToSignIn": "Continuar al inicio de sesión",
    "recovery.verifyNoToken": "A este enlace le falta el token. Solicita uno nuevo desde tu perfil.",
    "recovery.verifySuccess": "Tu correo electrónico ha sido confirmado. Ya puedes iniciar sesión.",
    "recovery.verifyFailed": "Este enlace de confirmación no es válido o ha expirado. Inicia sesión y solicita uno nuevo desde tu perfil.",
    "recovery.verifyChangeTitle": "Confirmando tu nuevo correo electrónico",
    "recovery.verifyChangeNoToken": "A este enlace le falta el token. Solicita uno nuevo desde tu perfil.",
    "recovery.verifyChangeSuccess": "Tu nueva dirección de correo está confirmada — inicia sesión con ella a partir de ahora.",
    "recovery.verifyChangeFailed": "Este enlace de confirmación no es válido o ha expirado. Inicia sesión y solicita un nuevo cambio de correo desde tu perfil.",

    // --- landing (pages/landing-page.ts) --------------------------------------------------------------
    "landing.nav.try": "Probar",
    "landing.nav.eventManager": "Para Organizadores",
    "landing.nav.rentCompany": "Para Empresas de Alquiler",
    "landing.nav.soundman": "Para Técnicos de Sonido",
    "landing.nav.howItWorks": "Cómo funciona",
    "landing.nav.contacts": "Contacto",
    "landing.nav.myRiders": "Mis riders",
    "landing.nav.login": "Iniciar sesión",
    "landing.nav.signup": "Regístrate gratis",
    "landing.hero.title": "Prepara tu espectáculo",
    "landing.hero.sub": "Servicio en línea para obtener todo el equipamiento necesario para tu show. Crea un rider técnico con todos los detalles y encuentra empresas de alquiler en un clic.",
    "landing.hero.cta": "Abrir el editor",
    "landing.hero.mediaAlt": "Editor de riders 3D SoundCheckBox",
    "landing.try.title": "Crea un rider ahora mismo",
    "landing.try.lead": "No hace falta cuenta. El editor se abre al instante y tu trabajo se queda en este navegador — la cuenta solo sirve para guardarlo, compartirlo o enviarlo.",
    "landing.try.cta": "Abrir el editor",
    "landing.eventManager.title": "Para organizadores de eventos",
    "landing.eventManager.body":
        "¿Gestionas un festival, una gira, un evento corporativo o privado? SoundCheckBox es el asistente que mantiene todos los acuerdos entre bandas y empresas de alquiler en un solo lugar.",
    "landing.eventManager.feature1Title": "Suministro de equipos",
    "landing.eventManager.feature1Desc": "Cada solicitud del rider se compara con el inventario real disponible de los proveedores.",
    "landing.eventManager.feature2Title": "Opciones de selección",
    "landing.eventManager.feature2Desc": "Compara propuestas lado a lado en lugar de enviar decenas de correos.",
    "landing.eventManager.feature3Title": "Control de acuerdos",
    "landing.eventManager.feature3Desc": "Visualiza la reputación de las empresas y reseñas de eventos anteriores antes de contratar.",
    "landing.eventManager.feature4Title": "Procesamiento de pagos",
    "landing.eventManager.feature4Desc": "Liquida y confirma reservas sin salir de la plataforma.",
    "landing.eventManager.mediaAlt": "Flujo de trabajo para organizadores",
    "landing.rentCompany.title": "Para empresas de alquiler",
    "landing.rentCompany.body":
        "Un gestor automatizado que lee los riders entrantes, los coteja con tu inventario y presupuesta el trabajo automáticamente.",
    "landing.rentCompany.feature1Title": "Cotejo de inventario",
    "landing.rentCompany.feature1Desc": "Los riders se comparan con tu stock real de almacén, no con una lista de deseos.",
    "landing.rentCompany.feature2Title": "Presupuesto automático",
    "landing.rentCompany.feature2Desc": "Cotizaciones calculadas a partir de tus tarifas tan pronto como llega un rider.",
    "landing.rentCompany.feature3Title": "Grandes operaciones",
    "landing.rentCompany.feature3Desc": "Diseñado para volúmenes altos de almacenamiento, con soporte de integración de software.",
    "landing.rentCompany.feature4Title": "Subalquiler",
    "landing.rentCompany.feature4Desc": "¿Te falta algún equipo? Te conectamos directamente con la empresa que lo tiene.",
    "landing.rentCompany.mediaAlt": "Flujo de trabajo para empresas de alquiler",
    "landing.soundman.title": "Para técnicos de sonido",
    "landing.soundman.body":
        "Menos papeleo, más tiempo para la música. Crea un rider utilizando configuraciones probadas y reutilízalas para cada artista que gestiones.",
    "landing.soundman.feature1Title": "Configuraciones probadas",
    "landing.soundman.feature1Desc": "Comienza desde un plano de escenario real en lugar de una página en blanco.",
    "landing.soundman.feature2Title": "Tus propios presets",
    "landing.soundman.feature2Desc": "Guarda un grupo de equipos personalizado una vez y úsalo para siempre.",
    "landing.soundman.feature3Title": "Gestión multi-banda",
    "landing.soundman.feature3Desc": "Mantén a todas las bandas que gestionas dentro de una sola cuenta.",
    "landing.soundman.feature4Title": "Sugerencias de paquetes",
    "landing.soundman.feature4Desc": "Las empresas de alquiler propondrán paquetes completos adaptados a tu rider.",
    "landing.soundman.mediaAlt": "Flujo de trabajo para técnicos de sonido",
    "landing.howItWorks.videoTitle": "SoundCheckBox — cómo funciona",
    "landing.howItWorks.lead":
        "Coloca los equipos en un escenario 3D y las listas de entradas, salidas y equipamiento se generarán automáticamente. Imprímelo o envíalo a la empresa de alquiler.",
    "landing.howItWorks.feature1Title": "1. Diseña el escenario",
    "landing.howItWorks.feature1Desc": "Arrastra presets —baterías, backline, micrófonos, monitores— a un escenario a escala.",
    "landing.howItWorks.feature2Title": "2. Las listas se escriben solas",
    "landing.howItWorks.feature2Desc": "Canales, fuentes y equipos se derivan automáticamente de lo que hay en el escenario.",
    "landing.howItWorks.feature3Title": "3. Compártelo",
    "landing.howItWorks.feature3Desc": "Imprime un rider A4 impecable con el plano del escenario o envíalo por enlace.",
    "landing.contact.title": "Contáctanos",
    "landing.contact.emailLabel": "Correo electrónico",
    "landing.contact.addressLabel": "Dirección",
    "landing.contact.followUs": "Síguenos",
    "landing.contact.nameFieldLabel": "Tu nombre",
    "landing.contact.emailFieldLabel": "Tu correo electrónico",
    "landing.contact.messageLabel": "Mensaje",
    "landing.contact.submit": "Enviar mensaje",
    "landing.contact.captchaNote": "Protegido por Cloudflare Turnstile — se aplican la {privacyLink} y los {termsLink} de Cloudflare.",
    "landing.contact.privacyPolicyLinkText": "Política de Privacidad",
    "landing.contact.termsLinkText": "Términos del Servicio",
    "landing.contact.validationError": "Por favor completa tu nombre, correo electrónico y mensaje.",
    "landing.contact.success": "Gracias — tu mensaje ha sido enviado. Responderemos a la dirección facilitada.",
    "landing.footer.tagline": "SoundCheckBox — constructor de riders técnicos",

    // --- riders list (pages/riders-list-page.ts) ------------------------------------------------------
    "riders.pageTitle": "Mis riders",
    "riders.newRiderButton": "Nuevo Rider",
    "riders.filter.groupLabel": "Filtrar Riders por estado",
    "riders.filter.all": "Todos",
    "riders.filter.empty": "No hay Riders que coincidan con este filtro.",
    "riders.table.title": "Título",
    "riders.table.band": "Banda / Artista",
    "riders.table.created": "Creado",
    "riders.table.changed": "Modificado",
    "riders.table.status": "Estado",
    "riders.table.action": "Acción",
    "riders.table.empty": "Aún no tienes riders — haz clic en “+ Nuevo Rider” para crear el primero.",
    "riders.status.published": "Publicado",
    "riders.status.draft": "Borrador",
    "riders.status.publicLinkTitle": "Enlace público: {url}",
    "riders.actions.edit": "Editar",
    "riders.actions.moreActionsAriaLabel": "Más acciones para {title}",
    "riders.actions.publish": "Publicar",
    "riders.actions.unpublish": "Despublicar",
    "riders.actions.print": "Imprimir",
    "riders.actions.copyPublicLink": "Copiar enlace público",
    "riders.actions.rename": "Renombrar",
    "riders.actions.duplicate": "Duplicar",
    "riders.copySuffix": "(copia)",
    "riders.copySuffixN": "(copia {n})",
    "riders.rename.heading": "Renombrar rider",
    "riders.rename.nameLabel": "Nuevo nombre",
    "riders.rename.confirm": "Renombrar",
    "riders.status.renaming": 'Renombrando "{title}"…',
    "riders.status.renamed": 'Renombrado a "{title}".',
    "riders.status.renameFailed": 'No se pudo renombrar "{title}": {message}',
    "riders.status.duplicating": 'Duplicando "{title}"…',
    "riders.status.duplicated": 'Se creó "{title}".',
    "riders.status.duplicateFailed": 'No se pudo duplicar "{title}": {message}',
    "riders.status.deleting": 'Eliminando "{title}"…',
    "riders.status.deleted": 'Se ha eliminado "{title}".',
    "riders.status.deleteFailed": 'Error al eliminar "{title}": {message}',
    "riders.status.linkCopied": "Enlace público copiado: {url}",
    "riders.status.unpublished": 'Se ha despublicado "{title}".',
    "riders.status.publishSuccess": 'Publicado "{title}" — enlace público: {url}',
    "riders.status.unpublishFailed": 'Error al despublicar "{title}": {message}',
    "riders.status.publishFailed": 'Error al publicar "{title}": {message}',
    "riders.status.publishedNoPicture": "La página pública no tiene imagen del plano de escenario: esta lista no puede dibujarla. Publica desde el editor para añadirla.",
    "riders.status.loading": "Cargando tus riders…",
    "riders.status.loadFailed": "Error al cargar tus riders: {message}",

    // --- profile (pages/profile-page.ts) ---------------------------------------------------------------
    "profile.pageTitle": "Mi perfil",
    "profile.personal.title": "Datos personales",
    "profile.personal.nameLabel": "Nombre",
    "profile.personal.phoneLabel": "Teléfono",
    "profile.personal.countryLabel": "País",
    "profile.personal.cityLabel": "Ciudad",
    "profile.personal.languageLabel": "Idioma",
    "profile.personal.save": "Guardar datos personales",
    "profile.work.title": "Datos profesionales",
    "profile.work.companyLabel": "Empresa / Compañía",
    "profile.work.roleLabel": "Soy",
    "roleChoice.hint": "Decide qué página se abre al iniciar sesión.",
    "roleChoice.hintOptional": "Opcional. Decide qué página se abre al iniciar sesión.",
    "profile.work.role.soundman": "Técnico de sonido",
    "profile.work.role.manager": "Organizador de eventos",
    "profile.work.role.rental": "Empresa de alquiler",
    "profile.work.role.other": "Otro",
    "profile.work.save": "Guardar datos profesionales",
    "profile.defaults.title": "Valores predeterminados del rider",
    "profile.defaults.hint": "Estos datos rellenan el bloque de contacto de cada NUEVO rider. Cada rider conserva sus valores finales.",
    "profile.defaults.bandNameLabel": "Nombre de la banda",
    "profile.defaults.contactNameLabel": "Nombre de contacto",
    "profile.defaults.contactPhoneLabel": "Teléfono de contacto",
    "profile.defaults.contactEmailLabel": "Correo de contacto",
    "profile.defaults.save": "Guardar valores predeterminados",
    "profile.security.title": "Seguridad",
    "profile.security.emailLabel": "Correo electrónico",
    "profile.security.emailConfirmedLabel": "Correo confirmado",
    "profile.security.passwordChangedLabel": "Último cambio de contraseña",
    "profile.security.memberSinceLabel": "Miembro desde",
    "profile.security.emailVerifiedYes": "Sí",
    "profile.security.emailVerifiedNot": "Aún no",
    "profile.security.resendVerification": "Reenviar correo de confirmación",
    "profile.security.changePasswordTitle": "Cambiar contraseña",
    "profile.security.changePasswordSubmit": "Cambiar contraseña",
    "profile.security.currentPasswordLabel": "Contraseña actual",
    "profile.security.newPasswordLabel": "Nueva contraseña",
    "profile.security.repeatPasswordLabel": "Repite la nueva contraseña",
    "profile.security.resendSent": "Enviado ✓",
    "profile.security.resendSuccess": "Correo de confirmación enviado. Revisa tu bandeja de entrada y spam.",
    "profile.security.passwordMismatch": "Las dos nuevas contraseñas no coinciden.",
    "profile.security.passwordChanged": "Contraseña modificada. Te hemos enviado un correo de confirmación.",
    "profile.security.changeEmailTitle": "Cambiar correo electrónico",
    "profile.security.changeEmailHint": "Enviaremos un enlace de confirmación a la nueva dirección — nada cambia hasta que hagas clic en él.",
    "profile.security.newEmailLabel": "Nuevo correo electrónico",
    "profile.security.currentPasswordForEmailLabel": "Contraseña actual",
    "profile.security.changeEmailSubmit": "Enviar enlace de confirmación",
    "profile.security.changeEmailMissingFields": "Introduce la nueva dirección de correo y tu contraseña actual.",
    "profile.security.changePasswordMissingFields": "Introduce tu contraseña actual y una nueva de al menos 8 caracteres.",
    "profile.security.changeEmailSuccess": "Si {newEmail} está disponible, un enlace de confirmación va en camino hacia allí. Revisa su bandeja de entrada y spam.",
    "profile.security.changeEmailPending": "Enviamos un enlace de confirmación a {newEmail}. Sigues conectado como {currentEmail} hasta que se confirme.",
    "profile.ai.title": "Asistente de IA",
    "profile.ai.hint": "El asistente tiene una pequeña asignación gratuita. Después, añade tu propia clave de Google Gemini para seguir usándolo — pagas a Google directamente, sin límite aquí.",
    "profile.ai.usageFree": "Asignación gratuita usada: {used} de {cap}.",
    "profile.ai.usageByok": "Usando tu propia clave ({hint}) — sin límite.",
    "profile.ai.byokHint": "Pega tu propia clave de Gemini API para seguir usando el asistente tras la asignación gratuita.",
    "profile.ai.getKeyLink": "Obtener una clave gratis",
    "profile.ai.keyLabel": "Tu clave de Gemini API",
    "profile.ai.keyReplace": "introduce una nueva clave para reemplazarla",
    "profile.ai.saveKey": "Guardar clave",
    "profile.ai.clearKey": "Quitar clave",
    "profile.ai.byokUnavailable": "La opción de clave propia aún no está disponible en este servidor.",
    "profile.ai.keyRequired": "Primero introduce tu clave de API.",
    "profile.ai.keyValidating": "Comprobando la clave…",
    "profile.ai.keySaved": "Clave guardada — el asistente ya la usa.",
    "profile.ai.keyCleared": "Clave eliminada.",
    "profile.ai.keySaveFailed": "No se pudo guardar la clave: {message}",
    "profile.ai.loadFailed": "No se pudo cargar tu uso de IA.",
    "profile.privacy.title": "Datos y privacidad",
    "profile.privacy.hint": "Tus derechos bajo el RGPD y leyes comparables.",
    "profile.privacy.downloadTitle": "Descargar mis datos",
    "profile.privacy.downloadHint": "Todo lo que almacenamos sobre ti, incluidos todos tus riders, en un único archivo JSON.",
    "profile.privacy.downloadButton": "Descargar",
    "profile.privacy.marketingTitle": "Novedades del producto",
    "profile.privacy.marketingHint":
        "Noticias ocasionales sobre SoundCheckBox. Los correos de servicio (restablecimiento de contraseña, confirmaciones) siempre se envían.",
    "profile.privacy.marketingCheckbox": "Deseo recibir correos sobre el producto",
    "profile.privacy.deleteTitle": "Eliminar mi cuenta",
    "profile.privacy.deleteHint": "Elimina permanentemente tu cuenta y todos los riders de tu propiedad.",
    "profile.privacy.deleteButton": "Eliminar cuenta",
    "profile.privacy.cancelDeleteButton": "Cancelar eliminación",
    "profile.status.loadFailed": "No se pudo cargar tu perfil: {message}",
    "profile.status.personalSaved": "Datos personales guardados.",
    "profile.status.workSaved": "Datos profesionales guardados.",
    "profile.status.defaultsSaved": "Valores predeterminados del rider guardados.",
    "profile.privacy.preparingExport": "Preparando tus datos…",
    "profile.privacy.exportDownloaded": "Tu archivo de datos ha sido descargado.",
    "profile.privacy.marketingOn": "Recibirás correos sobre el producto.",
    "profile.privacy.marketingOff": "Has sido cancelado de la lista de novedades.",
    "profile.privacy.deleteConfirm":
        "¿Eliminar tu cuenta de SoundCheckBox?\n\nTu cuenta y todos tus riders se borrarán permanentemente tras el periodo de gracia. Puedes cancelar el proceso desde esta página antes de esa fecha.",
    "profile.privacy.deletionScheduled": "Eliminación programada. Te hemos enviado los detalles por correo — aún puedes cancelarla.",
    "profile.privacy.deletionCancelled": "Eliminación cancelada. Tu cuenta permanece activa.",
    "profile.privacy.deletionPendingHint":
        "Programado para eliminación el {date}. Cancélalo en cualquier momento antes de esa fecha.",

    // --- public rider viewer (pages/public-viewer-page.ts) ---------------------------------------------
    "publicViewer.headerSub": "rider público",
    "publicViewer.riderListsAriaLabel": "Listas del rider",
    "publicViewer.controls.grid": "Cuadrícula",
    "publicViewer.controls.titles": "Títulos",
    "publicViewer.controls.inputCh": "Entradas (CH)",
    "publicViewer.controls.outputMix": "Salidas (MIX)",
    "publicViewer.controls.source": "Fuente",
    "publicViewer.controls.comments": "Comentarios",
    "publicViewer.window.close": "Cerrar ventana",
    "publicViewer.window.zoomIn": "Aumentar escala",
    "publicViewer.window.zoomOut": "Reducir escala",
    "publicViewer.controls.scene": "Escenario 3D",
    "publicViewer.controls.sceneLoading": "3D (cargando...)",
    "publicViewer.controls.sceneHint": "Ver el escenario en tres dimensiones: arrastra para orbitar a su alrededor.",
    "publicViewer.controls.plan": "Vista en planta",
    "publicViewer.controls.planHint": "Mirar el escenario desde arriba, desde el lado del público.",
    "publicViewer.controls.schematic": "Esquema B/N",
    "publicViewer.schematicLoading": "Dibujando el plano…",
    "publicViewer.schematicFailed": "No se pudo cargar la biblioteca de símbolos.",
    "publicViewer.controls.schematicHint": "Esquema en blanco y negro de alto contraste para impresión y lectura rápida.",
    "publicViewer.controls.schematicDisabledHint": "Aún no está listo: el plano en blanco y negro sigue en desarrollo.",
    "publicViewer.loaderAlt": "Cargando datos y modelos 3D...",
    "publicViewer.controls.hideStage": "Ocultar escenario",
    "publicViewer.controls.showStage": "Mostrar escenario",
    "publicViewer.controls.hideStageHint": "Ocultar el escenario para que las listas y el texto del rider ocupen toda la pantalla.",
    "publicViewer.controls.viewSwitchAriaLabel": "Vista del escenario",
    "publicViewer.controls.follow": "Acercar a lo seleccionado",
    "publicViewer.controls.followHint": "Al elegir un canal, la cámara vuela hasta el objeto de origen.",
    "publicViewer.controls.labels": "Etiquetas",
    "publicViewer.controls.fit": "Todo el escenario",
    "publicViewer.controls.fitHint": "Volver a encuadrar el escenario completo.",
    "publicViewer.controls.print": "Imprimir",
    "publicViewer.stageHint": "Arrastra para orbitar, pellizca/desplaza para hacer zoom, pulsa un objeto para seleccionarlo.",
    "publicViewer.stageHintPlan": "Arrastra para desplazar, pellizca/desplaza para hacer zoom, pulsa un objeto para seleccionarlo.",
    "publicViewer.planPosterAlt": "Plano del escenario renderizado cuando se publicó este rider",
    "publicViewer.deselectAriaLabel": "Deseleccionar",
    "publicViewer.tabs.rider": "Rider",
    "publicViewer.tabs.input": "Entradas",
    "publicViewer.tabs.output": "Salidas",
    "publicViewer.tabs.gear": "Equipos",
    "publicViewer.bandLogoAlt": "Logo de la banda",
    "publicViewer.table.ch": "Ch",
    "publicViewer.table.source": "Fuente",
    "publicViewer.table.type": "Tipo",
    "publicViewer.table.name": "Nombre",
    "publicViewer.table.comment": "Comentario",
    "publicViewer.table.category": "Categoría",
    "publicViewer.table.owner": "Propietario",
    "publicViewer.table.count": "Cantidad",
    "publicViewer.footer": "Creado con",
    "publicViewer.meta.inputCh": "Canales de entrada",
    "publicViewer.meta.outputMix": "Mezclas de salida",
    "publicViewer.meta.gear": "Equipos",
    "publicViewer.meta.gearCount": "{n} elemento(s)",
    "publicViewer.noRiderNotes": "Sin notas adicionales.",
    "publicViewer.untitledRiderHeading": "Rider sin título",
    "publicViewer.untitledRiderDocTitle": "Rider",
    "publicViewer.documentTitle": "{title} — SoundCheckBox",
    "publicViewer.contactLabel": "Contacto: {name}",
    "publicViewer.telLabel": "Tel: {phone}",
    "publicViewer.emailLabel": "Email: {email}",
    "publicViewer.status.noRiderId": "No se especificó un ID de rider — falta el parámetro en el enlace.",
    "publicViewer.status.withdrawn": "El autor retiró este rider y este enlace ya no funciona.",
    "common.deletePublishedPrompt": "¿Eliminar definitivamente? Su enlace público dejará de funcionar para todos los que lo tengan.",
    "common.yesDeleteAndUnpublish": "Sí, eliminar y desactivar el enlace",
    "publicViewer.status.notPublished": "Este rider no está publicado (o no existe). Solicita al propietario que lo publique.",
    "publicViewer.powerUnavailable": "Resumen de consumo eléctrico no disponible.",
    "publicViewer.controls.makeOffer": "Hacer oferta",
    "publicViewer.controls.makeOfferHint": "Responde la lista de canales y equipo de este rider como proveedor.",
    "publicViewer.controls.compact": "Vista compacta",
    "publicViewer.controls.compactHint": "El plano en blanco y negro con la lista corta de canales debajo.",
    "publicViewer.controls.io": "I/O",
    "publicViewer.controls.ioHint": "Mostrar los números de canal y de mezcla en el plano.",
    "publicViewer.controls.more": "Más",
    "publicViewer.controls.loadingWord": "(cargando...)",
    "publicViewer.phantomTitle": "Alimentación phantom (+48V)",
    "publicViewer.planFull.ariaLabel": "Abrir el plano a pantalla completa",
    "publicViewer.status.offerOwnRider": "No puedes hacer una oferta sobre tu propio rider.",
    "publicViewer.status.offerStartFailed": "No se pudo iniciar la oferta: {error}",

    // --- offer mode (pages/offer-overlay.ts, PR2 — docs/V2_OFFER_TECH_APPROVAL.md) -----------------------
    "offer.loading": "Cargando oferta…",
    "offer.loadFailed": "No se pudo cargar esta oferta: {error}",
    "offer.backToRider": "Volver al rider",
    "offer.status.draft": "Borrador",
    "offer.status.submitted": "Enviada",
    "offer.status.changesRequested": "Cambios solicitados",
    "offer.status.awaitingOrganiser": "Esperando al organizador",
    "offer.status.approved": "Aprobada",
    "offer.roundLabel": "Respuestas v{n}",
    "offer.commentLabel": "Comentario general",
    "offer.commentPlaceholder": "Cualquier cosa que el autor del rider deba saber sobre esta oferta en conjunto…",
    "offer.authorCommentHeading": "El autor del rider solicitó cambios",
    "offer.declinedReasonHeading": "Motivo del rechazo, del autor del rider",
    "offer.markRemainingOk": "Marcar el resto como OK",
    "offer.saveDraft": "Guardar borrador",
    "offer.submit": "Enviar oferta",
    "offer.saving": "Guardando…",
    "offer.savedStatus": "Borrador guardado.",
    "offer.submitting": "Enviando…",
    "offer.submitFailed": "No se pudo enviar: {error}",
    "offer.saveFailed": "No se pudo guardar: {error}",
    "offer.printFailed": "No se pudo imprimir: {error}",
    "offer.guest.notice": "No has iniciado sesión. Tus respuestas se guardan en este navegador hasta que envíes la oferta.",
    "offer.myList.button": "Comprobar con mi almacén",
    "offer.myList.empty": "Tu almacén está vacío: añade tu equipo en Almacén",
    "offer.myList.link": "Almacén",
    "offer.myList.summary": "{ok} líneas se pueden marcar OK con tu lista, en {short} líneas tienes menos de lo necesario, {missing} no están en tu lista",
    "offer.myList.apply": "Aplicar",
    "offer.myList.cancel": "Cancelar",
    "offer.myList.short": "tienes {have} de {need}",
    "offer.myList.marker": "de mi lista",
    "offer.myList.nothing": "Nada que comprobar: todas las líneas de equipo ya tienen respuesta.",
    "offer.guest.signInStep": "Para enviarla, inicia sesión o crea una cuenta — tus respuestas se conservan.",
    "offer.guest.signInButton": "Iniciar sesión o crear cuenta",
    "offer.guest.keepEditing": "Seguir editando",
    "offer.guest.emailLabel": "Tu correo",
    "offer.guest.emailSend": "Enviar oferta",
    "offer.guest.emailHint": "Te enviaremos un enlace por correo. Ábrelo y la oferta se envía, sin contraseña.",
    "offer.guest.emailInvalid": "Introduce una dirección de correo válida.",
    "offer.guest.emailSent": "Enviamos un correo a {email}. Abre el enlace: tu oferta se enviará sola. Puedes cerrar esta pestaña; tus respuestas se guardan en este navegador durante 48 horas.",
    "offer.guest.emailSendFailed": "No pudimos enviar el correo ahora. Inténtalo de nuevo en un minuto.",
    "offer.guest.emailAlreadySent": "El correo de confirmación ya se envió a {email}. Abre el enlace: tu oferta se enviará sola. Revisa también la carpeta de spam: un correo de un remitente desconocido suele caer ahí. Si no está, pídelo de nuevo en {minutes} min.",
    "offer.guest.emailSentAgain": "El correo de confirmación ya se envió a {email} y lo enviamos una vez más. Abre el enlace de cualquiera de los dos: tu oferta se enviará sola. Revisa también la carpeta de spam: un correo de un remitente desconocido suele caer ahí.",
    "offer.autoSending": "Enviando tu oferta…",
    "offer.autoSent": "Tu oferta se ha enviado.",
    "offer.awaitingEmail": "Enviamos un correo a {email}. Abre el enlace: tu oferta se enviará sola.",
    "offer.where.title": "Cuándo y dónde — para que la oferta llegue al show correcto",
    "offer.where.dateLabel": "Fecha",
    "offer.where.kind.range": "Periodo",
    "offer.where.kind.choice": "Varias fechas",
    "offer.where.kind.day": "Un día",
    "offer.where.pick.day": "Toca un día.",
    "offer.where.pick.range": "Toca el primer día y luego el último.",
    "offer.where.pick.choice": "Toca cada fecha posible: al menos dos.",
    "offer.where.clear": "Aún sin fecha",
    "offer.where.prevMonth": "Mes anterior",
    "offer.where.nextMonth": "Mes siguiente",
    "offer.where.chips": "Selección rápida",
    "offer.where.weekdays": "Días que valen",
    "offer.where.chip.thisWeek": "Esta semana",
    "offer.where.chip.nextWeek": "Próxima semana",
    "offer.where.chip.lastWeekOf": "Última semana de {month}",
    "offer.where.chip.wholeMonth": "Mes entero",
    "offer.where.possibleDays": "Días posibles: {n}",
    "offer.where.removeDate": "Quitar fecha",
    "offer.where.venue": "Sala",
    "offer.where.city": "Ciudad (opcional)",
    "offer.where.hint": "La banda ve tu oferta dentro de su show para esta fecha.",
    "offer.where.error.dayMissing": "Elige la fecha.",
    "offer.where.error.rangeIncomplete": "Toca el último día del periodo.",
    "offer.where.error.rangeBackwards": "El rango termina antes de empezar.",
    "offer.where.error.rangeNoDays": "Todos los días del rango están tachados: marca al menos un día de la semana.",
    "offer.where.error.choiceFew": "Elige al menos dos fechas o cambia a «Un día».",
    "offer.placed.whenVenue": "Tu oferta para este show ({when}, {venue}) se ha enviado. Los organizadores del show la están revisando, ¡gracias!",
    "offer.placed.when": "Tu oferta para este show ({when}) se ha enviado. Los organizadores del show la están revisando, ¡gracias!",
    "offer.placed.venue": "Tu oferta para este show ({venue}) se ha enviado. Los organizadores del show la están revisando, ¡gracias!",
    "offer.placed.none": "Tu oferta para este show se ha enviado. Los organizadores del show la están revisando, ¡gracias!",
    "offer.showLink.for": "Para: {parts}.",
    "offer.showLink.archived": "Este show ya está acordado: no admite más ofertas.",
    "offer.showLink.gone": "Este enlace del show no funciona: fue retirado o no se pudo leer. Pide a la banda uno nuevo.",
    "offer.send.title": "¿Enviar esta oferta?",
    "offer.send.rider": "Rider: {title}, v{version}",
    "offer.send.lines": "Líneas: {total}",
    "offer.send.comment": "Tu comentario general",
    "offer.send.ask": "Revisa: indica la fecha y la sala, o di que aún no están definidas.",
    "offer.send.notSet": "Fecha y sala aún sin definir",
    "offer.send.notSetOn": "Se envía con: fecha y sala aún sin definir.",
    "offer.send.or": "o",
    "offer.send.send": "Enviar",
    "offer.send.back": "Volver a la oferta",
    "offer.send.close": "Cerrar",
    "offer.send.need": "Indica una fecha y una sala, o pulsa «Fecha y sala aún sin definir».",
    "offer.send.viaLink": "La fecha y la sala vienen del enlace del show — no hay nada que rellenar.",
    "offer.said.notSet": "Fecha y sala: aún sin definir (respuesta del proveedor)",
    "riders.actions.publishToShow": "Publicar en un show",
    "publishShow.title": "Publicar en un show",
    "publishShow.hint": "Un show tiene fecha y sala. Quien abra el enlace responde exactamente a este show.",
    "publishShow.whenWhere": "Cuándo y dónde",
    "publishShow.rider": "Rider: {title}, v{version}",
    "publishShow.create": "Crear show y enlace",
    "publishShow.creating": "Creando el show…",
    "publishShow.linkLabel": "Enlace para proveedores",
    "publishShow.copy": "Copiar",
    "share.action": "Enviar",
    "share.hint": "Envía este rider por correo, WhatsApp, Telegram o cualquier mensajería",
    "share.email": "Correo",
    "share.whatsapp": "WhatsApp",
    "share.telegram": "Telegram",
    "share.copyLink": "Copiar enlace",
    "share.copied": "Enlace copiado",
    "share.kind": "rider técnico",
    "publishShow.copied": "Copiado",
    "publishShow.close": "Cerrar",
    "publishShow.notPublished": "Publica primero el rider: un show apunta a una versión publicada.",
    "publishShow.failed": "No se pudo crear el show: {error}",
    "showPage.about": "Sobre este show",
    "showPage.when": "Fecha",
    "showPage.venue": "Sala",
    "showPage.rider": "Rider",
    "showPage.riderValue": "{title}, v{version}",
    "showPage.dateOpen": "Fecha aún sin acordar",
    "showPage.venueNone": "Sala aún sin elegir",
    "showPage.makeOffer": "Hacer una oferta",
    "showPage.archived.past": "Este show ya tuvo lugar. Esta página es su archivo: no admite más ofertas.",
    "showPage.archived.agreed": "Este show está acordado. Esta página es su archivo: no admite más ofertas.",
    "showPage.repeat": "Repetir este show",
    "showPage.loadFailed": "No se pudo leer este show: {error}",
    "repeatShow.title": "Repetir este show",
    "repeatShow.hint": "Un show nuevo con la misma sala y los mismos artistas. El original queda como está.",
    "repeatShow.whenWhere": "Nueva fecha",
    "repeatShow.rider.newer": "El rider tiene una versión más reciente (v{newest}; se tocó v{played})",
    "repeatShow.rider.quiet": "El rider no ha cambiado desde el show (v{played}).",
    "repeatShow.rider.newest": "Usar la más reciente",
    "repeatShow.rider.keep": "Mantener la que se tocó",
    "repeatShow.rider.ask": "Pedir a la banda uno nuevo",
    "repeatShow.offers.title": "Ofertas",
    "repeatShow.offers.count": "Ofertas aprobadas en este show: {n}",
    "repeatShow.offers.invite": "Invitar de nuevo a la misma empresa",
    "repeatShow.offers.none": "Empezar sin ofertas",
    "repeatShow.create": "Crear el nuevo show",
    "repeatShow.creating": "Creando el show…",
    "repeatShow.needAnswer": "Responde las preguntas de arriba para continuar.",
    "repeatShow.done": "El nuevo show está creado. Este es su enlace para proveedores:",
    "repeatShow.failed": "No se pudo repetir el show: {error}",
    "emailLogin.title": "Iniciando sesión",
    "emailLogin.checking": "Comprobando tu enlace…",
    "emailLogin.failed": "Este enlace ha caducado o ya se usó. Abre el rider de nuevo y pulsa Enviar: tus respuestas se guardan en este navegador durante 48 horas.",
    "emailLogin.noToken": "Este enlace está incompleto. Ábrelo de nuevo desde el correo.",
    "emailLogin.success": "Has iniciado sesión.",
    "emailLogin.offerFailed": "No se pudo entregar la oferta que enviaste con tu correo; quizá el rider se retiró. Abre el rider y envíala de nuevo.",
    "emailLogin.usedTitle": "Este enlace ya se usó",
    "emailLogin.usedBody": "Si enviaste una oferta con él, la oferta se envió: está en la cuenta de {email}. Te enviaremos un enlace nuevo a la misma dirección: abre tu cuenta con tu oferta, y allí puedes crear una contraseña.",
    "emailLogin.usedBodyNoEmail": "Si enviaste una oferta con este enlace, se envió. Escribe tu correo y te enviaremos un enlace nuevo a tu cuenta.",
    "emailLogin.emailLabel": "Tu correo",
    "emailLogin.sendNew": "Enviarme un enlace nuevo",
    "emailLogin.signIn": "Ya tengo una cuenta: iniciar sesión",
    "emailLogin.newSent": "Enviamos un enlace nuevo a {email}. Ábrelo: entrarás en tu cuenta, junto a tu oferta.",
    "setPassword.title": "Crea una contraseña para volver más tarde",
    "setPassword.hint": "Tu cuenta se creó con el enlace del correo. Con una contraseña podrás abrirla cuando quieras y ver tu oferta y la respuesta del autor.",
    "setPassword.label": "Nueva contraseña (al menos 8 caracteres)",
    "setPassword.button": "Crear contraseña",
    "setPassword.tooShort": "La contraseña debe tener al menos 8 caracteres.",
    "setPassword.done": "Contraseña creada. La próxima vez, inicia sesión con tu correo y esta contraseña.",
    "setPassword.failed": "No se pudo crear la contraseña. Inténtalo de nuevo.",
    "offer.validation.modelRequired": "Indica un modelo para cada respuesta \"Sustituto\" antes de guardar.",
    "offer.validation.reasonRequired": "Indica un motivo para cada respuesta \"N/D\" antes de guardar.",
    "offer.readOnlyNotice": "Esta oferta está en estado «{status}» y ya no se puede editar.",
    "offer.table.answer": "Respuesta",
    "offer.bandBringsNote": "Lo trae la banda",
    "offer.gear.consumables": "Consumibles — de los kits confirmados",
    "offer.gear.cablesElsewhere": "Los cables figuran en su propia sección más abajo.",
    "offer.notConnectedNote": "No conectado",
    "offer.answer.unanswered": "—",
    "offer.answer.ok": "OK",
    "offer.answer.markOk": "Confirmar como OK",
    "offer.answer.substitute": "Sustituto",
    "offer.answer.bandCarried": "Lo trae la banda",
    "offer.answer.na": "N/D",
    "offer.answer.venueProvided": "Lo pone el recinto",
    "offer.answer.modelPlaceholder": "Modelo",
    "offer.alt.warehouseEmpty": "No tiene nada en esta rúbrica",
    "offer.alt.searchCatalogue": "Buscar en nuestro catálogo",
    "offer.answer.reasonPlaceholder": "Motivo",

    "offer.answer.commentPlaceholder": "Comentario",
    "offer.answer.short.ok": "OK",
    "offer.answer.short.substitute": "Alt",
    "offer.answer.short.bandCarried": "Banda",
    "offer.answer.short.na": "N/D",
    "offer.answer.short.venueProvided": "Recinto",
    "offer.collapseAll": "Contraer todo",
    "offer.expandAll": "Expandir todo",
    "offer.patchShow": "Parchear este show →",
    "offer.confirmedOf": "{done} de {total} confirmados",
    "offer.summary.confirmed": "confirmado",
    "offer.summary.alternatives": "con alternativa",
    "offer.summary.unavailable": "no disponible",
    "offer.summary.open": "sin responder",
    "offer.summary.nothingYet": "Aún sin respuestas",
    "offer.allConfirmed": "Todo confirmado",
    "offer.sectionToggle": "Abrir o cerrar esta sección",
    "offer.plan.title": "Plano de escenario",
    "offer.plan.io": "I/O",
    "offer.plan.ioShow": "Mostrar rótulos de canales y mezclas",
    "offer.plan.ioHide": "Ocultar rótulos de canales y mezclas",
    "offer.plan.open": "Toca para abrir a pantalla completa",
    "offer.plan.close": "Cerrar",
    "offer.plan.zoomIn": "Acercar",
    "offer.plan.zoomOut": "Alejar",
    "offer.plan.loading": "Dibujando el plano…",
    "offer.plan.failed": "No se pudo dibujar el plano.",
    "offer.wire.checkPatch": "Revisar el parcheo del show y las longitudes de cable →",
    "offer.wire.hint": "Abre el editor de parcheo y el cálculo de cables",
    "offer.wire.longMic": "Cables de micrófono largos",
    "offer.wire.shortPatch": "Cables de parcheo cortos",
    "offer.detail.each": "Cada uno de {n} — para qué sirve",
    "offer.detail.kit": "kit {name}",
    "offer.detail.kitHide": "kit {name}",
    "offer.amp.summary": "{n} × LA4X amplified controller · {capacity} output channels · usados {used} · libres {free}",
    "offer.amp.capacity": "drive capacity por LA4X: 2 × 115XT HiQ o 4 × SB18 (L-Acoustics)",
    "offer.amp.channels": "{n} can.",
    "offer.amp.twoWay": "2-way active (LF + HF)",
    "offer.amp.oneChannel": "1 output channel",
    "offer.amp.used": "{used}/{capacity} can.",
    // --- offer review mode (pages/offer-review-overlay.ts, PR3 — docs/V2_OFFER_TECH_APPROVAL.md) -----
    "offer.review.supplierCommentLabel": "Comentario del proveedor",
    "offer.review.noComment": "Sin comentario.",
    "offer.review.yourRequestHeading": "Cambios que solicitaste",
    "offer.review.approvedNotice": "Esta oferta ha sido aprobada. Ya no es posible hacer cambios.",
    "offer.review.approve": "Aprobar",
    "offer.review.requestChanges": "Solicitar cambios",
    "offer.review.requestChangesCommentLabel": "¿Qué hay que cambiar?",
    "offer.review.requestChangesCommentPlaceholder": "Indica al proveedor qué corregir antes de reenviar…",
    "offer.review.sendRequest": "Enviar",
    "offer.review.approving": "Aprobando…",
    "offer.review.approveFailed": "No se pudo aprobar: {error}",
    "offer.review.sending": "Enviando…",
    "offer.review.requestFailed": "No se pudo enviar: {error}",
    "offer.review.commentRequired": "Indica qué hay que cambiar antes de enviar.",
    "offer.review.supplierNameUnknown": "Proveedor desconocido",
    "offer.review.historyHeading": "Historial",
    "offer.review.history.submitted": "Enviado",
    "offer.review.history.changesRequested": "Cambios solicitados",
    "offer.review.history.approved": "Aprobado",
    "offer.status.declined": "Rechazada",
    "offer.review.decline": "Rechazar",
    "offer.review.declinedNotice": "Esta oferta fue rechazada. El proveedor no puede modificarla ni reenviarla.",
    "offer.review.declineConfirm": "¿Rechazar esta oferta? Es definitivo: el proveedor será avisado y no podrá reenviarla.",
    "offer.review.declineReasonLabel": "Motivo (opcional)",
    "offer.review.declineReasonPlaceholder": "Se mostrará al proveedor en el correo…",
    "offer.review.declineConfirmButton": "Rechazar oferta",
    "offer.review.declining": "Rechazando…",
    "offer.review.declineFailed": "No se pudo rechazar: {error}",
    "offer.review.history.declined": "Rechazada",

    // --- /offers — the two-sided offer inbox (pages/offers-page.ts, PR3) -----------------------------
    "offers.pageTitle": "Ofertas",
    "offers.incomingHeading": "Ofertas sobre mis riders",
    "offers.mineHeading": "Mis ofertas",
    "offers.incomingEmpty": "Todavía nadie ha respondido a uno de tus riders publicados.",
    "offers.mineEmpty": "Aún no has hecho una oferta sobre un rider.",
    "offers.table.rider": "Rider",
    "offers.table.supplier": "Proveedor",
    "offers.table.supplierUnknown": "Proveedor desconocido",
    "offers.table.status": "Estado",
    "offers.table.round": "Ronda",
    "offers.table.updated": "Actualizado",
    "offers.status.loading": "Cargando…",
    "offers.status.loadFailed": "No se pudieron cargar las ofertas: {error}",

    // --- editor topbar (components/topbar.ts) -----------------------------------------------------------
    "topbar.loggedInAs": "sesión iniciada como {email}",
    "topbar.adminBadge": "admin",
    "topbar.adminBadgeTitle": "Tienes privilegios de administrador (p. ej. Subir objetos 3D).",
    "topbar.unsavedRiderId": "(nuevo rider sin guardar)",
    "topbar.save.title": "Guardar Rider en la base de datos ({mod}+S)",
    "topbar.save.copyTitle":
        "Guardar como mi propia copia — este rider pertenece a otra persona, al guardar se almacenará un NUEVO rider en tu cuenta ({mod}+S)",
    "topbar.save.copyLabel": "Guardar como mi propia copia",
    "topbar.saveAs.title": "Guardar como… — guarda el rider abierto como uno NUEVO con el nombre que elijas, sin tocar el original ({mod}+Shift+S)",
    "topbar.saveAs.label": "Guardar como…",
    "topbar.attach.title": "Vincular objeto a otro — activa el modo y luego haz clic en el objeto padre",
    "topbar.attach.label": "Vincular objeto a otro",
    "topbar.attach.armedTitle": "Modo vinculación activo — haz clic en el objeto en la escena 3D que será el padre (Escape para cancelar).",
    "topbar.attach.armedLabel": "Selecciona un objetivo…",
    "topbar.publish.label": "Publicar rider",
    "topbar.publish.titleUnsaved": "Publicar rider — primero guarda el rider",
    "topbar.publish.titleNotMine": "Publicar rider — solo el propietario puede publicar este rider",
    "topbar.publish.titlePublished": "Publicado como v{version} — enlace público: {url} (clic para despublicar)",
    "topbar.publish.unpublishLabel": "Despublicar rider",
    "topbar.publish.titleDefault": "Publicar rider — hazlo accesible mediante un enlace público de lectura (para código QR al imprimir)",

    // --- editor page chrome (pages/editor-page.ts, EDITOR_PAGE_HTML template) ----------------------------
    "editor.riderIdTitle": "ID del rider guardado (vacío = nuevo)",
    "editor.menuToggleTitle": "Contraer/expandir el menú",
    "editor.menuToggleLabel": "Menú",
    "editor.overflowMenuTitle": "Más",
    "editor.rightPanelCollapseTitle": "Contraer panel",
    "editor.rightPanelExpandTitle": "Expandir panel",
    "editor.rightPanelResizerTitle": "Arrastra para cambiar el ancho del panel (doble clic para restablecer el ancho automático)",
    "editor.undoTitle": "Deshacer ({mod}+Z)",
    "editor.undoLabel": "Deshacer",
    "editor.redoTitle": "Rehacer ({mod}+Shift+Z)",
    "editor.redoLabel": "Rehacer",
    "editor.rotateTitle": "Rotar (R) — activa el anillo de rotación y arrástralo. Por defecto rota el grupo completo; mantén Alt para rotar solo el objeto.",
    "editor.rotateLabel": "Rotar",
    "editor.attachArmedHint": "Haz clic en el objeto al que unir «{title}»: un podio, un soporte · Esc para cancelar",
    "editor.modifierAltHint": "Alt — solo este objeto",
    "editor.modifierAltStackHint": "Alt — toda la pila",
    "editor.modifierShiftHint": "Shift — haz clic en los objetos para agruparlos, suelta para terminar (o suelta sobre un objeto al arrastrar para pegar)",
    "editor.uploadObjectTitleDisabled": "Subir objeto 3D — solo administradores",
    "editor.uploadObjectTitleAdmin": "Subir objeto 3D — crear un nuevo preset global a partir de un archivo .glb",
    "editor.uploadObjectLabel": "Subir objeto 3D",
    "editor.deleteObjectLabel": "Eliminar objeto seleccionado",
    "editor.deleteObjectTitle": "Eliminar objeto seleccionado (Alt+Delete)",
    "editor.savePresetLabel": "Guardar preset",
    "editor.deleteSubtreeLabel": "Eliminar objeto con todos sus elementos hijos",
    "editor.deleteSubtreeTitle": "Eliminar objeto con todos sus elementos hijos (Delete)",
    "editor.selectMode.groupLabel": "Toda la pila",
    "editor.selectMode.groupTitle": "Selección: toda la pila — arrastrar, girar y eliminar afectan al grupo (mantén Alt para un solo objeto)",
    "editor.selectMode.itemLabel": "Un objeto",
    "editor.selectMode.itemTitle": "Selección: un objeto — arrastrar, girar y eliminar afectan al objeto que has pulsado (mantén Alt para toda la pila)",
    "editor.deleteInGroupLabel": "Eliminar toda la pila",
    "editor.deleteInGroupTitle": "Eliminar toda la pila (Delete) — Alt: solo el objeto pulsado",
    "editor.deleteInItemLabel": "Eliminar este objeto",
    "editor.deleteInItemTitle": "Eliminar el objeto pulsado (Delete) — Alt: toda la pila",
    "editor.deleteGroupConfirm.heading": "¿Eliminar toda la pila?",
    "editor.deleteGroupConfirm.body": "«{title}» se eliminará del escenario junto con todo lo que tiene encima. Objetos: {n}.",
    "editor.deleteGroupConfirm.confirmLabel": "Eliminar la pila",
    "editor.showAnnotationsLabel": "Mostrar/ocultar anotaciones",
    "editor.annotationOptionsTitle": "Opciones de contenido de anotaciones",
    "editor.annotationContentAriaLabel": "Contenido de anotaciones",
    "editor.labelsPlaqueStrip": "LABELS",
    "editor.annotationCh": "CH",
    "editor.topViewTitle": "Vista superior",
    "editor.topViewLabel": "Vista superior",
    "editor.planViewTitle": "Plano",
    "editor.planViewLabel": "Plano",
    "editor.view3dLabel": "3D",
    "editor.view3dTitle": "Vista 3D",
    "editor.topViewShort": "Top",
    "editor.planViewShort": "Plan",
    "editor.viewSwitcherAriaLabel": "Vista",
    "editor.viewSwitcherOptionsTitle": "Opciones de vista",
    "editor.planViewLoading": "Dibujando el plano…",
    "editor.planViewFailed": "No se pudo cargar la biblioteca de símbolos.",
    "editor.status.planMoveRefused": "Este objeto no se puede mover ahí.",
    "presetMenu.editMode": "Editar",
    "presetMenu.editModeDone": "Listo",
    "presetMenu.reorderFailed": "No se pudo guardar el orden: {error}",
    "presetMenu.setDefaultOrder": "Fijar como orden predeterminado",
    "presetMenu.defaultOrderSaved": "Este es ahora el orden predeterminado para quienes no hayan ordenado los suyos.",
    "editor.annotationSource": "Fuente",
    "editor.annotationMix": "MIX",
    "editor.annotationTitle": "Título",
    "editor.annotationDesc": "Descripción",
    "editor.showGridLabel": "Mostrar/ocultar cuadrícula de coordenadas",
    "editor.printLabel": "Imprimir rider",
    "editor.printTitle": "Exportar a PDF e imprimir",
    "editor.exitLabel": "Salir a Mis Riders",
    "editor.riderOperationsAriaLabel": "Operaciones del rider",
    "editor.tabs.properties": "Properties",
    "editor.tabs.propertiesTitle": "Properties (E)",
    "editor.tabs.rider": "Rider",
    "editor.tabs.inputList": "Input List",
    "editor.tabs.inputListTitle": "Input List (I)",
    "editor.tabs.outputList": "Output List",
    "editor.tabs.outputListTitle": "Output List (O)",
    "editor.tabs.gearList": "Gear List",
    "editor.riderTab.heading": "Rider",
    "editor.riderTab.titleLabel": "Título",
    "editor.riderTab.commentLabel": "Comentarios",
    "editor.riderTab.stageWidthLabel": "Ancho (m)",
    "editor.riderTab.stageDepthLabel": "Profundidad (m)",
    "editor.riderTab.useBorderLabel": "Usar límites del escenario",
    "editor.riderTab.useBorderHint": "(desactivado = permitir objetos fuera del escenario — FOH, decoración)",
    "editor.riderTab.contactLegend": "Contacto y banda",
    "editor.riderTab.moduleRider": "Rider",
    "editor.riderTab.moduleStage": "Escenario",
    "editor.riderTab.moduleBand": "Banda",
    "editor.riderTab.moduleContact": "Contacto",
    "editor.riderTab.contactHint": "(autocompletado desde tu perfil; se guarda con este rider)",
    "editor.riderTab.bandNameLabel": "Nombre de la banda",
    "editor.riderTab.bandLogoAlt": "Logo de la banda",
    "editor.riderTab.bandLogoFileLabel": "Logo de la banda",
    "editor.riderTab.removeLogo": "Eliminar logo",
    "editor.riderTab.contactNameLabel": "Nombre de contacto",
    "editor.riderTab.contactPhoneLabel": "Teléfono de contacto",
    "editor.riderTab.contactEmailLabel": "Correo de contacto",
    "editor.riderTab.saveDefaultsTitle":
        "Guardar estos contactos como tus valores predeterminados — rellenarán cada nuevo rider que crees",
    "editor.riderTab.saveDefaultsLabel": "Guardar como predeterminados",
    "editor.riderTab.riderTextLabel": "Texto del rider",
    "editor.riderTab.savedDefaultsStatus": "Guardado como predeterminado — los nuevos riders comenzarán con esta información.",
    "editor.riderTab.savedDefaultsFailed": "No se pudieron guardar los valores predeterminados: {message}",
    "editor.listHint": "(rider completo — arrastra una fila para reordenar; Ch = su posición. Clic en una fila para seleccionar el objeto.)",
    "editor.reorderAriaLabel": "Reordenar",
    "editor.table.ch": "Ch",
    "editor.table.source": "Fuente",
    "editor.table.type": "Tipo",
    "editor.table.chName": "Nombre canal",
    "editor.table.micDi": "Micro / DI",
    "editor.table.out": "OUT",
    "editor.table.comment": "Comentario",
    "editor.table.name": "Nombre",
    "editor.table.category": "Categoría",
    "editor.table.owner": "Propietario",
    "editor.table.weight": "Peso",
    "editor.table.count": "Cantidad",
    "editor.inputTab.heading": "Input List (Entradas)",
    "editor.inputTab.addButton": "Añadir canal de entrada",
    "editor.outputTab.heading": "Output List (Salidas)",
    "editor.outputTab.addButton": "Añadir línea de salida",
    "editor.gearTab.heading": "Gear List (Equipamiento)",
    "editor.gearTab.hint": "(rider completo — equipos de cada objeto. Clic en una fila para seleccionar su objeto.)",
    "editor.gearTab.addButton": "Añadir elemento",
    "editor.params.closeTitle": "Deseleccionar / cerrar",
    "editor.params.emptyHint": "Select an object on the stage to see its properties.",
    "editor.params.heading": "Objeto seleccionado",
    "editor.params.nameLabel": "Nombre",
    "editor.params.nameHint": "(clave de catálogo, solo lectura)",
    "editor.params.titleLabel": "Título",
    "editor.params.titleToSourceLabel": "Título a la Fuente",
    "editor.params.commentLabel": "Comentario",
    "editor.params.parentLabel": "Padre",
    "editor.params.xLabel": "X",
    "editor.params.yLabel": "Y",
    "editor.params.zLabel": "Z",
    "editor.params.zTitle": "Altura sobre el escenario (m). Para objetos decorativos fuera del plano del escenario, p. ej. una pantalla o cortinas.",
    "editor.params.turnLabel": "Ángulo de giro°",
    "editor.params.lockPositionLabel": "Bloquear posición",
    "editor.params.lockChildrenLabel": "Bloquear elementos hijos",
    "editor.params.structureLabel": "Estructura",
    "editor.params.structureHint": "(se pueden colocar objetos encima)",
    "editor.params.heightLabel": "Altura, m",
    "editor.params.heightHint": "(altura de la superficie)",
    "editor.params.riserAcrossLabel": "Ancho, m",
    "editor.params.riserDeepLabel": "Fondo, m",
    "editor.params.riserHeightsLabel": "Alturas rápidas, m",
    "editor.params.riserResizeLabel": "Mover y cambiar el tamaño en el escenario",
    "presetMenu.riserTooltipHint": "doble clic para cambiar el tamaño",
    "editor.params.riserResizeDoneLabel": "Terminar",
    "editor.params.riserResizeHint": "Arrastre el objeto para moverlo y los tiradores para cambiar el tamaño. Mayús = libre, Intro o Esc = terminar.",
    "editor.params.stageMoveLabel": "Mover en el escenario",
    "editor.params.stageMoveHint": "Arrástrelo en el escenario para moverlo. Mayús = libre, Intro o Esc = terminar.",
    "editor.params.anchoredHint": "Anclado — doble clic para mover o cambiar el tamaño",
    "editor.params.anchoredMoveHint": "Anclado — doble clic para mover",
    "editor.params.anchoredLockedHint": "Bloqueado — desmarque «Bloquear posición» para moverlo",
    "editor.params.snapToGridLabel": "Ajustar a la cuadrícula",
    "editor.params.snapNeedsGridHint": "El ajuste solo funciona con la cuadrícula visible (el botón de cuadrícula de la barra superior). Con la cuadrícula oculta, todo se mueve libremente.",
    "editor.params.snapToGridHint": "Los bordes se ajustan a la cuadrícula de lo que compone el objeto: panel de 0,5 m en la tarima, cabinet en la pantalla LED, tramo estándar en la cercha. Desactivado: queda exactamente donde lo coloque.",
    "riser.handle.deep": "Arrastre para cambiar el fondo",
    "riser.handle.across": "Arrastre para cambiar el ancho",
    "riser.handle.corner": "Arrastre para cambiar el ancho y el fondo",
    "riser.handle.height": "Arrastre para cambiar la altura (pasos de 0,2 m)",
    "riser.handle.dock": "Añadir un praticable idéntico en este lado",
    "riser.readout": "{across} × {deep} m · A {height} m",
    "stage.handle.deep": "Arrastre para cambiar el fondo del escenario",
    "stage.handle.across": "Arrastre para cambiar el ancho del escenario",
    "stage.handle.corner": "Arrastre para cambiar el ancho y el fondo del escenario",
    "stage.readout": "Escenario {across} × {deep} m",
    "riser.status.docked": "Se ha añadido un praticable idéntico al lado de este.",
    "ledWall.handle.cols": "Arrastre para añadir o quitar columnas de gabinetes",
    "ledWall.handle.rows": "Arrastre para añadir o quitar filas de gabinetes",
    "construction.led.cabinetLabel": "Gabinete, mm",
    "construction.led.cabinetCustom": "{w} × {h} (propio)",
    "construction.led.colsLabel": "Columnas",
    "construction.led.rowsLabel": "Filas",
    "construction.led.pitchLabel": "Pitch de píxel, mm",
    "construction.led.mountLabel": "Montaje",
    "construction.led.mountGround": "Apilada en suelo",
    "construction.led.mountFlown": "Volada",
    "construction.led.bottomLabel": "Borde inferior, m",
    "construction.led.contentLabel": "Contenido",
    "construction.led.contentOff": "Apagada (negro)",
    "construction.led.contentGrid": "Retícula de gabinetes",
    "construction.led.contentBars": "Barras de color",
    "construction.led.contentLogo": "Cartela con logo",
    "construction.led.contentImage": "Imagen",
    "construction.led.contentMedia": "Medios",
    "construction.led.chooseImage": "Elegir imagen…",
    "construction.led.removeImage": "Quitar imagen",
    "construction.led.imageSet": "Imagen puesta en la pantalla LED ({w} × {h} px).",
    "construction.led.imageTooBig": "La imagen es demasiado grande incluso reducida. Elige una más pequeña.",
    "construction.led.imageUnreadable": "No se pudo leer este archivo como imagen.",
    "construction.led.imageRemoved": "Imagen quitada de la pantalla LED.",
    "construction.led.mediaLabel": "Medios",
    "construction.led.mediaLogo": "Logotipo de SoundCheckBox",
    "construction.led.mediaMascot": "Mascota",
    "construction.led.mediaIntro": "Intro",
    "construction.led.mediaIntroPlain": "Intro (sin dinero)",
    "construction.led.mediaTutorial": "Vídeo tutorial",
    "construction.led.mediaCustomUpload": "Subido: {name}",
    "construction.led.mediaCustomLink": "Enlace: {host}",
    "construction.led.mediaUrlLabel": "Enlace directo (https)",
    "construction.led.mediaUrlPlaceholder": "https://…/video.mp4",
    "construction.led.mediaUrlUse": "Mostrar este enlace",
    "construction.led.mediaUpload": "Subir vídeo o imagen…",
    "construction.led.mediaChecking": "Comprobando el enlace…",
    "construction.led.mediaUploading": "Subiendo…",
    "construction.led.mediaSet": "La pantalla muestra ahora lo elegido.",
    "construction.led.mediaProblemEmpty": "Pega un enlace a un archivo de vídeo o imagen.",
    "construction.led.mediaProblemStreaming": "Las páginas de {site} no se pueden mostrar en la pantalla: {site} solo reproduce vídeo dentro de su propio reproductor. Usa un enlace directo al archivo (.mp4 o .webm) o sube el archivo.",
    "construction.led.mediaProblemNotHttps": "El enlace debe empezar por https://",
    "construction.led.mediaProblemCredentials": "Quita el usuario y la contraseña del enlace.",
    "construction.led.mediaProblemNotAddress": "Esto no es una dirección web. Pega el enlace completo, que empieza por https://",
    "construction.led.mediaProblemTooLong": "Este enlace es demasiado largo.",
    "construction.led.mediaFailCors": "Este sitio no permite mostrar su vídeo en otras páginas.",
    "construction.led.mediaFailUnreachable": "La dirección no respondió. Revisa el enlace y tu conexión.",
    "construction.led.mediaFailHttp": "La dirección respondió con un error ({status}): el archivo no está o no es público.",
    "construction.led.mediaFailNotMedia": "Esa dirección es una página web o una lista de emisión, no un archivo de vídeo o imagen. Usa un enlace directo a un archivo .mp4, .webm, .png, .jpg o .gif.",
    "construction.led.mediaFailFormat": "El navegador no pudo reproducir este archivo. Usa vídeo mp4 (H.264) o webm, o imágenes png, jpeg o gif.",
    "construction.led.mediaUploadForbidden": "Solo los administradores pueden subir contenido.",
    "construction.led.mediaUploadType": "En la pantalla solo se pueden poner archivos mp4, webm, png, jpeg y gif.",
    "construction.led.mediaUploadEmpty": "El archivo está vacío.",
    "construction.led.mediaUploadTooBig": "El archivo es demasiado grande: vídeos hasta {video} MB, imágenes hasta {image} MB.",
    "construction.led.mediaUploadFailed": "No se pudo subir. Inténtalo de nuevo.",
    "construction.led.derivedSize": "{size} m",
    "construction.led.derivedPx": "{px} px",
    "construction.led.derivedCount": "gabinetes: {count}",
    "construction.led.derivedLoad": "aprox. {kg} kg · aprox. {kw} kW máx.",
    "construction.truss.typeLabel": "Tipo",
    "construction.truss.typeLadder": "Plana (escalera)",
    "construction.truss.typeTriangle": "Triangular",
    "construction.truss.typeBox": "Cuadrada",
    "construction.truss.profileLabel": "Perfil, mm",
    "construction.truss.lengthLabel": "Longitud, m",
    "construction.truss.trimLabel": "Altura (cara inferior), m",
    "construction.truss.supportLabel": "Sujeción",
    "construction.truss.supportFlown": "Volada (motores)",
    "construction.truss.supportFloor": "En el suelo",
    "construction.truss.supportGoalpost": "Portería (dos torres)",
    "construction.truss.sections": "Tramos: {sections}",
    "construction.truss.towers": "torres 2 × {h} m",
    "construction.truss.longSpan": "luz larga — revisa la tabla de cargas",
    "editor.params.listsInputs": "Entradas",
    "editor.params.listsOutputs": "Salidas",
    "editor.params.listsGear": "Equipo",
    "editor.params.moduleObject": "Objeto",
    "editor.params.modulePosition": "Posición",
    "editor.params.listPosition": "posición",
    "editor.params.listPositions": "posiciones",
    "editor.params.actionsLegend": "Acciones del objeto",
    "editor.params.rotateHint":
        "Rota con la herramienta <strong>Rotar</strong> de la barra superior o introduce el ángulo exacto en <strong>Ángulo de giro</strong>.",
    "editor.params.addChildLabel": "Añadir hijo",
    "editor.params.deleteObjectLabel": "Eliminar objeto",
    "editor.params.savePresetTitle": "Guardar este objeto y sus elementos hijos como un preset reutilizable",
    "editor.params.savePresetLabel": "Guardar como preset",
    "editor.params.addInputLabel": "Añadir nueva Entrada",
    "editor.params.addOutputLabel": "Añadir nueva Salida",
    "editor.params.linkOutputLabel": "Vincular a salida",
    "editor.params.linkOutputTitle": "Vincular este objeto a una línea de salida existente o número de canal",
    "editor.params.linkOutputDisabledTitle": "No hay otras salidas disponibles en el escenario para vincular",
    "editor.params.addGearLabel": "Añadir nuevo Equipo",
    "editor.presetCategoriesAriaLabel": "Categorías de presets",
    "editor.exitConfirm.title": "¿Salir sin guardar?",
    "editor.exitConfirm.body": "Tienes cambios sin guardar en este rider. Si sales ahora se perderán.",
    "editor.exitConfirm.saveAndLeave": "Guardar y salir",
    "editor.exitConfirm.leaveWithoutSaving": "Salir sin guardar",

    // --- editor page behaviour (pages/editor-page.ts, JS-built status messages) ---------------------------
    "editor.status.gluedToGroup": "Unido al grupo: ahora {members} objetos se mueven juntos.",
    "editor.status.glueNoTarget": "No hay nada bajo el punto de suelta: mantén Shift sobre un objeto para unirte a su grupo.",
    "editor.status.glueFailed": "No se pudo unir al grupo: {reason}",
    "editor.status.detached": "Sacado de su grupo: ahora se mueve por su cuenta.",
    "editor.status.detachNotGrouped": "Este objeto no está en un grupo.",
    "editor.status.multiSelectCount": "{count} seleccionados para agrupar — suelta Shift para terminar.",
    "editor.status.grouped": "Se agruparon {count} objetos.",
    "editor.status.groupFailed": "No se pudo agrupar: {reason}",
    "editor.status.attachCancelled": "Vinculación cancelada.",
    "editor.status.attachFailed": "No se pudo vincular: {reason}",
    "editor.status.attached": "Vinculado {source} a {target}.",
    "editor.status.undo": "Deshecho.",
    "editor.status.redo": "Rehecho.",
    "editor.status.attachArmed": "Modo vinculación activo — haz clic en el objeto que será el padre de {name} (Escape para cancelar).",
    "editor.status.savingCopy": "Guardando tu propia copia…",
    "editor.status.savedAsCopy":
        "Guardado como un NUEVO rider — tu copia propia. El original pertenece a otro usuario y no ha cambiado.",
    "editor.status.saved": "Guardado a las {time}.",
    "editor.status.guestDraftRestored": "Tu trabajo anterior está aquí — se guarda en este navegador hasta que lo guardes en una cuenta.",
    "editor.status.guestDraftDiscarded":
      "El trabajo guardado en este navegador no se pudo abrir de forma segura, así que se ha descartado en lugar de intentarlo de nuevo. Empezando con un rider nuevo y vacío.",
    "editor.status.guestDraftNotSaved":
      "Tu navegador no pudo guardar los cambios aquí (puede que el almacenamiento esté lleno). Mantén esta pestaña abierta, o inicia sesión y guarda para conservar tu trabajo.",
    "editor.draft.offer": "Cambios sin guardar de {time}.",
    "editor.draft.continue": "Restaurar estos cambios",
    "editor.draft.discard": "Dejar lo que está abierto",
    "editor.draft.close": "Cerrar: decidiré luego",
    "editor.naming.offer": "Guardado. Este rider aún se llama «New rider» y la banda no tiene nombre: por ahí lo buscará después.",
    "editor.naming.name": "Ponerles nombre",
    "editor.naming.later": "Más tarde",
    "editor.draft.offerDiverged": "Tienes cambios sin guardar de {time}, pero este rider se volvió a guardar en el servidor después. Restaurarlos encima sobrescribiría un trabajo más reciente.",
    "editor.draft.openAsCopy": "Abrir mis cambios como copia",
    "editor.draft.useSaved": "Usar la versión guardada",
    "editor.draft.restored": "Tus cambios sin guardar han vuelto. Pulsa Guardar para conservarlos.",
    "editor.draft.restoredAsCopy": "Tus cambios están abiertos como un rider nuevo sin guardar. El original guardado no se ha tocado. Pulsa Guardar para conservar la copia.",
    "editor.draft.notKept": "Tu navegador no pudo conservar una copia de recuperación de tus cambios (el almacenamiento puede estar lleno). Pulsa Guardar para no perderlos si se cierra tu sesión.",
    "editor.draft.crashed": "Los cambios sin guardar de este navegador no se pudieron abrir de forma segura, así que se descartaron en lugar de reintentarlos.",
    "editor.status.modelsUnavailable": "Algunos modelos 3D no se han cargado — los objetos se muestran como cajas etiquetadas. Tu trabajo está a salvo.",
    "editor.status.presetsUnavailable": "No se pudo cargar el catálogo de objetos — puedes seguir dibujando, pero el menú de presets está vacío. Recarga para reintentar.",
    "editor.status.guestSaveNeedsAccount": "Inicia sesión o regístrate para guardar este rider — tu trabajo está a salvo y te acompaña.",
    "editor.status.guestSaveFailed": "No se pudo guardar en tu cuenta: {message}. Tu trabajo sigue aquí — pulsa Guardar para reintentar.",
    "editor.status.saveFailed": "Error al guardar: {message}",
    "editor.saveAs.heading": "Guardar como rider nuevo",
    "editor.saveAs.nameLabel": "Nombre",
    "editor.saveAs.confirm": "Guardar una copia",
    "editor.saveAs.hint": "El rider que tienes abierto se queda igual: esto guarda una copia aparte con el nombre que le des.",
    "editor.saveAs.untitled": "Rider sin título",
    "editor.status.savedAsNamedCopy": '"{title}" se guardó como un rider nuevo. El rider que tenías abierto no ha cambiado.',
    "editor.status.unpublished": "Despublicado — todos los enlaces públicos previos para este rider ya no funcionarán.",
    "editor.status.published": "Publicado como v{version} — enlace público: {url}",
    "editor.status.publishedNoPicture": "Publicado, pero no se pudo crear la imagen del plano: vuelva a publicar sin salir de esta pestaña y el lector verá el escenario mientras cargan los modelos.",
    "editor.status.copiedSuffix": " (copiado al portapapeles)",
    "editor.status.publishFailed": "Error al publicar: {message}",
    "publishProgress.publishing": "Publicando el rider…",
    "publishProgress.drawing": "Publicado como v{version}. Dibujando el plano de escenario para la página pública…",
    "publishProgress.done": "Publicado como v{version}: la página pública está lista, con el plano de escenario.",
    "publishProgress.published": "Publicado como v{version}: la página pública está lista.",
    "publishProgress.noPicture": "Publicado como v{version}, pero no se pudo dibujar el plano de escenario. Los lectores verán el escenario cuando carguen sus modelos. Vuelve a publicar sin salir de esta pestaña para añadir la imagen.",
    "publishProgress.failed": "Error al publicar: {message}",
    "publishProgress.open": "Abrir la página pública",
    "editor.status.ioKeptNote": " Sus canales se mantuvieron como marcadores de posición vacíos.",
    "editor.status.ioOptimizedNote": " Sus canales fueron eliminados y la numeración se reajustó.",
    "editor.status.deletedWithChildren": "Eliminado {title}; {count} objeto(s) hijo(s) conservados y reasignados.",
    "editor.status.deleted": "Eliminado {title}.",
    "editor.status.deletedSubtree": "Eliminado {title} y todos sus elementos hijos.",
    "editor.status.rotated": "Rotado {name} {deg}° ({mode}).",
    "editor.status.rotateModeObject": "solo objeto; hijos retenidos",
    "editor.status.rotateModeSet": "grupo completo",
    "editor.status.moved": "Movido {name} a X {x}, Y {y}.",
    "editor.status.dropMissed": "Caída fuera del escenario — intenta soltar más cerca de la plataforma.",
    "editor.status.loadingRider": "Cargando el rider…",
    "editor.status.loaded": "Cargado «{title}» — {count} objetos en el escenario.",
    "editor.status.loadedNotMineSuffix":
        " Este rider pertenece a otra persona — puedes editarlo libremente, pero al Guardar se creará una NUEVA copia propia.",
    "editor.status.loadFailed": "Error de carga: {message}",
    "editor.status.newRider": "Nuevo rider (aún no guardado).",

    // --- object-params panel behaviour (components/object-params-panel.ts) -------------------------------
    "objectParams.status.reparentFailed": "No se pudo cambiar de padre: {reason}",
    "objectParams.status.moved": "Movido {name} bajo {location}.",
    "objectParams.status.locationNewParent": "un nuevo padre",
    "objectParams.status.locationTopLevel": "el nivel superior",
    "objectParams.status.lockedChildren": "Bloqueada la posición en todos los descendientes de {name}.",
    "objectParams.status.reseated": "Reubicados {n} objeto(s) situados sobre {name}.",
    "objectParams.status.addedInput": "Añadido un canal de entrada a {name}.",
    "objectParams.status.addedOutput": "Añadida una línea de salida a {name}.",
    "objectParams.status.addedGear": "Añadido un elemento de equipo a {name}.",
    "objectParams.status.addedChild": "{childName} añadido a {name}.",
    "objectParams.status.removed": "{name} eliminado. Los canales de los objetos restantes permanecen intactos.",
    "objectParams.noParentOption": "(sin padre — nivel superior)",

    // --- preset menu (components/preset-menu.ts) ----------------------------------------------------------
    "presetMenu.loading": "Cargando presets…",
    "presetMenu.loadFailed": "No se pudo cargar el catálogo de objetos. Recarga la página para volver a intentarlo.",
    "presetMenu.countSuffix": "({count})",
    "presetMenu.searchPlaceholder": "Buscar en el catálogo",
    "presetMenu.searchNoMatch": "Nada coincide con «{q}»",
    "presetMenu.noMatch": "Ningún preset coincide con el filtro.",
    "presetMenu.onlyMine": "Solo míos",
    "presetMenu.added": 'Preset "{name}" añadido al escenario.',
    "presetMenu.removeFromCategory": "Quitar de {category}",
    "presetMenu.removedFromCategory": "{name} quitado de {category}. El preset sigue intacto.",
    "presetMenu.removeFromCategoryFailed": "No se pudo quitar {name} de {category}: {message}",
    "presetMenu.deleteLastCategoryPrompt": "Es su única categoría. Eliminarlo borra el preset definitivamente y no se puede deshacer. ¿Eliminar?",
    "presetMenu.deleteAlsoFromPrompt": "Borra el preset definitivamente: también desaparecerá de {others} y no se puede deshacer. ¿Eliminar?",
    "presetMenu.deleting": 'Eliminando preset "{name}"…',
    "presetMenu.deleted": 'Eliminado el preset "{name}".',
    "presetMenu.deleteFailed": 'Error al eliminar el preset "{name}": {message}',
    "presetMenu.uncategorizedCategory": "Sin categoría",
    "presetMenu.chooseCategoryPlaceholder": "— asignar categoría —",
    "presetMenu.assignedCategory": 'Preset "{name}" asignado a la categoría "{category}".',
    "presetMenu.assignCategoryFailed": 'No se pudo asignar la categoría al preset "{name}": {message}',

    // --- Patch Editor (components/patch-editor-dialog.ts) — SCRUM-241 ---------------------------------------
    "patchEditor.title": "Patch Editor y Conexiones de Escenario",
    "patchEditor.subtitle": "Enrutamiento físico para {count} canal(es) a través de cajas de escenario",
    "patchEditor.stageBoxesHeading": "StageBoxes Digitales y Racks",
    "patchEditor.routingTableHeading": "Tabla de Enrutamiento de Canales",
    "patchEditor.colChannel": "Canal",
    "patchEditor.colSource": "Fuente",
    "patchEditor.colSubSnake": "Sub-Snake / Drop Box",
    "patchEditor.colSocket": "Conector",
    "patchEditor.colStageBox": "StageBox",
    "patchEditor.colBoxInput": "Entrada Rack",
    "patchEditor.colCableLength": "Cable",
    "patchEditor.cableSummaryHeading": "Lista de Cables (Especificación para baúl)",
    "patchEditor.printSheet": "Imprimir Hoja de Patch",
    "patchEditor.close": "Cerrar",
    "patchEditor.done": "Hecho",
    "topbar.patchEditor": "Patch",
    "topbar.patchEditor.disabledTooltip": "Patch Editor — Disponible en modo Show / Gira cuando el rider está asignado a un recinto con cajas de escenario y consolas.",
    "topbar.patchEditor.activeTooltip": "Abrir Patch Editor (P)",

    // --- I/O keep-on-delete dialog (components/io-delete-dialog.ts) ----------------------------------------
    "ioDelete.heading": "¿Mantener la numeración de canales o reajustar la lista?",
    "channelOverflow.heading": "No hay canales libres suficientes",
    "channelOverflow.summary": "«{preset}» necesita {need} canales de entrada. Hay {free} libres ({slots}): faltan {short}.",
    "channelOverflow.movingLine": "CH {ch} — {source} / {name}",
    "channelOverflow.movingMore": "{lines} … y {n} más",
    "channelOverflow.nothingToMove": "No hay nada por encima de los canales libres: desplazar no mueve nada.",
    "channelOverflow.appendLabel": "Añadir al final (CH {channels})",
    "channelOverflow.shiftLabel": "Desplazar desde CH {from} en {n}",
    "channelOverflow.cancelled": "«{name}» no se añadió: cancelaste la pregunta sobre los canales.",
    "ioDelete.optimizeLabel": "Optimizar I/O",
    "ioDelete.keepLabel": "Mantener como está",
    "ioDelete.rememberLabel": "No volver a preguntar (recordar esta elección)",
    "ioDelete.inputChannelSingular": "{n} canal de entrada",
    "ioDelete.inputChannelPlural": "{n} canales de entrada",
    "ioDelete.outputChannelSingular": "{n} canal de salida",
    "ioDelete.outputChannelPlural": "{n} canales de salida",
    "ioDelete.summary": "El objeto que vas a eliminar contiene {parts}.",

    // --- row-edit dialog (components/row-edit-dialog.ts) — docs/V2_IO_KEEP_ON_DELETE.md §3c --------------
    "rowEdit.headingInput": "Editar canal de entrada",
    "rowEdit.headingOutput": "Editar línea de salida",
    "rowEdit.sourceLabel": "Fuente",
    "rowEdit.chNameLabel": "Nombre del canal",
    "rowEdit.typeLabel": "Tipo",
    "rowEdit.commentLabel": "Comentario",
    "rowEdit.lockedLabel": "Bloqueado",
    "rowEdit.chHashLabel": "Ch #",
    "rowEdit.lockedHint":
      "El canal de una línea bloqueada no se puede cambiar. Desmarca Bloqueado para moverla y luego guarda — desbloquear y mover ocurren juntos, en un solo guardado.",
    "rowEdit.lockedHintInput": "El número de un canal bloqueado no se puede cambiar. Desmarca Bloqueado para moverlo y luego guarda — desbloquear y mover ocurren juntos, en un solo guardado.",
    "rowEdit.linkedLabel": "También conectados (mezcla compartida)",
    "rowEdit.linkedHint": "Otros monitores alimentados por esta misma mezcla — p. ej. dos cuñas en un envío. También mostrarán el número de canal de esta línea en el plano de escenario.",
    "rowEdit.linkedEmpty": "Todavía no hay otros monitores en el escenario.",
    "rowEdit.saveLabel": "Guardar",
    "rowEdit.deleteChannel": "Eliminar canal",
    "rowEdit.deleteLine": "Eliminar línea",
    "rowEdit.errorInvalidChannel": "El canal debe ser un número mayor o igual a 1.",
    "rowEdit.errorChannelTaken": "El canal {channel} ya está en uso por otra línea — elige un número libre.",
    "rowEdit.errorLineGone": "Esta línea ya no existe — cierra este cuadro e inténtalo de nuevo.",
    "rowEdit.errorChannelGone": "Este canal ya no existe — cierra este cuadro e inténtalo de nuevo.",

    // --- link to existing output dialog (components/link-output-dialog.ts) ------------------
    "linkOutput.heading": "Vincular a salida existente",
    "linkOutput.currentlyLinkedLabel": "Salidas vinculadas actualmente:",
    "linkOutput.selectOutputLabel": "Seleccionar línea de salida:",
    "linkOutput.chooseOutputOption": "Elegir una salida existente...",
    "linkOutput.manualOption": "Introducir número de canal manualmente...",
    "linkOutput.manualChannelLabel": "Número de canal:",
    "linkOutput.submitLabel": "Vincular",
    "linkOutput.unlinkButtonTitle": "Desvincular esta salida",
    "linkOutput.ownerSuffix": "propietario de esta salida",
    "linkOutput.alreadyLinkedSuffix": "ya vinculado",
    "linkOutput.errorSelectChannel": "Por favor, selecciona una salida o introduce un número de canal válido.",
    "linkOutput.error.invalidChannel": "Introduzca un número de canal igual o mayor que 1.",
    "linkOutput.error.nodeNotFound": "Ese objeto ya no está en el escenario. Cierre esta ventana y vuelva a seleccionarlo.",
    "linkOutput.error.alreadyOwner": "Esta salida ya pertenece al objeto: no hay nada que enlazar.",
    "linkOutput.error.channelNotFound": "Todavía no hay ninguna salida en ese canal. Créela primero con «Add new Output».",
    "linkOutput.errorGeneric": "No se pudo vincular a la salida.",
    "linkOutput.status.linked": "Se vinculó {name} a la Salida {channel}",
    "linkOutput.status.unlinked": "Se desvinculó {name} de la Salida {channel}",

    // --- save-preset dialog (components/save-preset-dialog.ts) ---------------------------------------------
    "savePreset.heading": "Guardar como preset",
    "savePreset.nameLabel": "Nombre del preset",
    "savePreset.categoryLabel": "Categoría",
    "savePreset.categoryRequiredHint": "(obligatorio)",
    "savePreset.saveAsLabel": "Guardar como",
    "savePreset.iconLegend": "Icono",
    "savePreset.renderingHint": "Generando imagen del objeto…",
    "savePreset.useRenderedIcon": "Usar captura 3D",
    "savePreset.useOriginalIcon": "Usar icono original del preset",
    "savePreset.clearUploaded": "Limpiar archivo subido",
    "savePreset.submitLabel": "Guardar preset",
    "savePreset.usingUploadedImage": "Usando tu imagen subida.",
    "savePreset.usingOriginalPresetIcon": "Usando la imagen original del preset.",
    "savePreset.autoGenerated": "Autogenerado a partir del objeto 3D — sube un archivo para reemplazarlo.",
    "savePreset.iconPreviewAlt": "Vista previa del icono del preset",
    "savePreset.chooseCategoryOption": "— elige una categoría —",
    "savePreset.newPresetOption": "Nuevo preset",
    "savePreset.overwriteOption": "Sobrescribir: {title}",
    "savePreset.sharedLibrarySuffix": " (biblioteca compartida)",
    "savePreset.confirmOverwriteButton": "Sí, sobrescribir",
    "savePreset.backToEdit": "Atrás",
    "savePreset.overwriteWarning":
      'Sobrescribir reemplaza "{title}" — cada futura inserción usará tu nueva versión. Los riders que ya lo usaron conservan su propia copia de cuando se añadió, así que no cambiarán.',
    "savePreset.overwriteWarningSharedNote": "Este preset forma parte de la biblioteca compartida — tu actualización será visible para todos.",
    "savePreset.subject": "{title} — {count} objeto(s), se guarda con todo su árbol secundario.",
    "savePreset.renderFailed": "No se pudo generar el icono — el preset se guardará sin él.",
    "savePreset.errorNodeGone": "El objeto seleccionado ya no existe — cierra esta ventana y vuelve a seleccionar un objeto.",
    "savePreset.errorNoCategory": "Elige una categoría — un preset sin categoría no aparecerá en el menú.",
    "savePreset.errorStaleTarget": "Ya no puedes sobrescribir ese preset — vuelve a abrir el diálogo e inténtalo de nuevo.",
    "savePreset.updated": 'Actualizado el preset "{title}" (id {id}) — el cambio ya está disponible en tu menú.',
    "savePreset.saved": 'Guardado el preset "{title}" (id {id}) — ya aparece en tu menú de presets.',
    "savePreset.saveFailed": "No se pudo guardar el preset: {message}",
    "savePreset.imageReadFailed": "No se pudo leer la imagen: {message}",

    // --- Asistente de IA (components/ai-assistant-dialog.ts) ---
    "ai.buttonTitle": "Asistente de IA: describe un cambio con palabras",
    "ai.buttonLabel": "IA",
    "ai.heading": "Asistente de IA",
    "ai.subheading": "Describe un cambio en tu escenario en lenguaje natural. Revisarás cada edición antes de aplicarla.",
    "ai.promptLabel": "Tu solicitud",
    "ai.promptPlaceholder": "p. ej. añade una batería al fondo del escenario en el centro y un ampli de bajo a su izquierda",
    "ai.generate": "Generar",
    "ai.record": "Hablar",
    "ai.recordTitle": "Di tu solicitud por voz (hasta 20 s)",
    "ai.recording": "Grabando… toca el micrófono para detener.",
    "ai.stopRecording": "Detener la grabación",
    "ai.micUnsupported": "La entrada por voz no es compatible con este navegador.",
    "ai.micDenied": "Se denegó el acceso al micrófono; permítelo en el navegador para usar la voz.",
    "ai.micEmpty": "No se grabó audio; inténtalo de nuevo.",
    "ai.generating": "Pensando…",
    "ai.previewHeading": "Cambios propuestos",
    "ai.apply": "Aplicar cambios",
    "ai.discard": "Descartar",
    "ai.emptyPrompt": "Primero escribe qué quieres cambiar.",
    "ai.noChanges": "El asistente no propuso ningún cambio. Intenta reformular tu solicitud.",
    "ai.notConfigured": "El asistente de IA aún no está habilitado en este servidor.",
    "ai.capReached": "Has agotado tu asignación gratuita de IA. Añade tu propia clave de Gemini en tu perfil para seguir usando el asistente.",
    "ai.providerError": "El servicio de IA no está disponible temporalmente. Inténtalo de nuevo en un momento.",
    "ai.invalid": "El asistente no pudo producir una edición válida para este rider. Intenta reformular.",
    "ai.invalidReference": "El asistente se refirió a un objeto o preset que no está en este escenario. Nómbralo de otra forma o añádelo primero.",
    "ai.someSkipped": "No se pudieron aplicar {count} cambio(s) (referían algo que no está en el escenario o no cabía) y se omitieron:",
    "ai.op.add": "Añadir un objeto",
    "ai.op.move": "Mover un objeto",
    "ai.op.rotate": "Girar un objeto",
    "ai.op.delete": "Eliminar un objeto",
    "ai.op.settings": "Actualizar ajustes del escenario",
    "ai.op.unknown": "Una operación",
    "ai.failed": "No se pudo contactar con el asistente de IA. Revisa tu conexión e inténtalo de nuevo.",
    "ai.applied": "Se aplicaron {count} cambio(s) del asistente.",
    "ai.diff.addPreset": "Añadir {preset} en ({x}, {y}) m",
    "ai.diff.moveObject": "Mover {object} a ({x}, {y}) m",
    "ai.diff.rotateObject": "Girar {object} {deg}°",
    "ai.diff.deleteObject": "Eliminar {object}",
    "ai.diff.deleteTree": "Eliminar {object} y sus {count} objeto(s) hijo(s)",
    "ai.diff.setRiderMeta": "Actualizar ajustes del rider: {fields}",
    "ai.diff.metaField.title": "título",
    "ai.diff.metaField.comment": "comentario",
    "ai.diff.metaField.width": "ancho del escenario",
    "ai.diff.metaField.length": "profundidad del escenario",

    // --- upload-object dialog (components/upload-object-dialog.ts) -----------------------------------------
    "uploadObject.heading": "Subir objeto 3D",
    "uploadObject.hint": "Solo administradores. Crea un nuevo preset global desde un modelo glTF binario (.glb).",
    "uploadObject.nameLabel": "Nombre",
    "uploadObject.modelLabel": "Archivo de modelo (.glb)",
    "uploadObject.categoryLabel": "Categoría",
    "uploadObject.iconLabel": "Icono (opcional)",
    "uploadObject.heightLabel": "Altura, m",
    "uploadObject.structureLabel": "Es una estructura (tarimas/plataformas donde se pueden apilar objetos)",
    "uploadObject.submitLabel": "Subir y crear preset",
    "uploadObject.errorNameRequired": "El nombre es obligatorio.",
    "uploadObject.errorModelRequired": "Selecciona un archivo de modelo .glb.",
    "uploadObject.errorNotGlb": "El modelo debe ser un archivo .glb.",
    "uploadObject.errorNoCategory": "Elige una categoría.",
    "uploadObject.uploading": "Subiendo…",
    "uploadObject.uploaded": 'Subido "{name}" — ya está disponible en la biblioteca de presets.',

    // --- list tables (components/list-tables.ts, editor/dom-utils.ts) --------------------------------------
    "dom.deleteLineTitle": "Eliminar esta línea",
    "dom.editLineTitle": "Editar esta línea",
    "dom.editChannelTitle": "Editar este canal",
    "dom.deleteChannelTitle": "Eliminar este canal",
    "listTables.unconnectedRowTitle":
        "Sin conexión — el objeto de este canal fue eliminado; el número se mantiene reservado. Elimina esta fila para liberarlo.",
    "listTables.unconnectedChannelTitle": "Sin conexión — el objeto de este canal fue eliminado; el número se mantiene reservado. Elimínalo para liberar el número.",
    "listTables.phantomTitle": "Alimentación Fantasma (+48V) — clic para alternar",
    "listTables.dragHandleTitle":
        "Arrastra para reordenar — la posición en la lista determina el número de canal. Ctrl/Cmd- o Shift-clic previo para mover varias filas juntas.",
    "listTables.clearSelectionTitle": "Clic para quitar la selección",
    "listTables.columnGripTitle": "{name} — arrastra el borde para cambiar el ancho; doble clic en el borde para ajustar",
    "listTables.panelGripTitle": "Arrastra para ajustar el ancho del panel: el mismo borde que el del panel",
    "listTables.noneOption": "(ninguno)",
    "listTables.categoryFromCatalogue": "El catálogo dice qué es esto. Para cambiarlo, cambia el modelo, no la etiqueta.",
    "listTables.categoryStale": "Esta línea apunta a la rúbrica {ref} del catálogo, que ya no existe. Elige el modelo de nuevo.",
    "listTables.gearUngrouped": "Fuera de los kits",
    "listTables.selectionBadge": "{count} seleccionados — arrastra cualquiera para mover el grupo",
    "listTables.linkLabel": "Enlazar",
    "listTables.unlinkLabel": "Desenlazar",
    "listTables.pickModeLabel": "Seleccionar varias",
    "listTables.linkTitle": "Enlazar las dos líneas seleccionadas como par estéreo",
    "listTables.unlinkTitle": "Desenlazar el par seleccionado (los nombres se mantienen)",
    "listTables.linkNeedTwo": "Selecciona dos líneas contiguas para enlazarlas como par estéreo",
    "listTables.linkNotAdjacent": "Solo se pueden enlazar líneas contiguas (canal N y N+1)",
    "listTables.linkAlreadyPaired": "Una de estas líneas ya está en un par: desenlázala primero",
    "listTables.unlinkNothing": "Selecciona un par enlazado para desenlazarlo",
    "listTables.pairMarkTitle": "Par estéreo enlazado — canales {a} y {b}",
    "ioDelete.pairSummary": "Esta línea es una mitad de un par estéreo. La otra mitad se queda y pasa a ser una línea simple (mono).",
    "listTables.powerApprox": '≈{watts} W por unidad — coincidencia aproximada de catálogo ("{name}"), el consumo puede diferir.',
    "listTables.powerExact": "{watts} W por unidad, según el catálogo de equipos: {name}.",
    "listTables.powerLoading": "Cargando datos de consumo eléctrico…",
    "listTables.powerError": "Catálogo de equipos no disponible — consumo total no calculado.",
    "listTables.notFoundInCatalog": "No encontrado en el catálogo: {names}",

    // --- power summary (editor/power.ts — shared by the Gear tab AND the printed rider) --------------------
    "power.noGearItems": "Aún no hay elementos de equipamiento.",
    "power.totalLine": "Escenario / FOH: {backline} W · Sistema de PA: {pa} W",
    "power.fromLine": "— calculado para {resolved} de {items} elementos.",
    "picker.catalogLoading": "Cargando el catálogo…",
    "picker.samePurpose": "mismo uso",
    "picker.family": "afín",
    "picker.startTyping": "Escribe un modelo o un fabricante",
    "picker.keepTyped": "Dejar «{text}» como está escrito",
    "picker.classAnswers": "Modelos que responden a «{name}»",
    "gear.notInCatalog": "no está en el catálogo",
    "power.itemSingular": "{n} elemento",
    "power.itemPlural": "{n} elementos",
    "power.zeroPowerSingular": "{items} no consume energía eléctrica.",
    "power.zeroPowerPlural": "{items} no consumen energía eléctrica.",
    "power.unresolvedLine": "{items} no encontrado en el catálogo (no contabilizado).",

    // --- print document (editor/print-rider.ts) -------------------------------------------------------------
    "printRider.noInputChannels": "Sin canales de entrada.",
    "printRider.noOutputChannels": "Sin canales de salida.",
    "printRider.noGearItems": "Sin lista de equipamiento.",
    "printRider.headers.ch": "Ch",
    "printRider.headers.v48": "48V",
    "printRider.headers.source": "Fuente",
    "printRider.headers.micDi": "Mic / DI",
    "printRider.headers.type": "Tipo",
    "printRider.headers.owner": "Propietario",
    "printRider.headers.comment": "Comentario",
    "printRider.headers.send": "Envío",
    "printRider.headers.category": "Categoría",
    "printRider.headers.name": "Nombre",
    "printRider.headers.count": "Cantidad",
    "printRider.headers.belongsTo": "Pertenece a",
    "printRider.headers.offer": "OFFER",
    "printRider.gear.bandBrings": "Lo trae el grupo — no hay que pedirlo",
    "printRider.gear.allCables": "Todos los cables necesarios para conectar el equipo indicado en este rider",
    "printRider.gear.bandNote": "Este material estará en el escenario: lo trae el grupo. No hay que suministrar nada de aquí.",
    "printRider.gear.ungrouped": "Fuera de los kits",
    "printRider.gear.noCategory": "Sin categoría",
    "gearList.section.pa": "P.A.",
    "gearList.section.amplification": "Amplificación",
    "gearList.section.mixing": "Sistema de mezcla",
    "gearList.section.backline": "Backline",
    "gearList.section.monitoring": "Monitoreo",
    "gearList.section.instruments": "Instrumentos",
    "gearList.section.mics": "Micrófonos y DI",
    "gearList.section.stands": "Pies y soportes",
    "gearList.section.stage": "Estructuras de escenario",
    "gearList.section.power": "Distribución eléctrica",
    "gearList.section.wire": "Cables",
    "gearList.section.consumables": "Consumibles",
    "gearList.standFor.microphone": "Pies de micrófono",
    "gearList.standFor.guitar-bass": "Soportes para guitarra y bajo",
    "gearList.standFor.keyboard": "Soportes para teclado",
    "gearList.standFor.wind": "Soportes para instrumentos de viento",
    "gearList.standFor.drums-percussion": "Soportes de batería y percusión",
    "gearList.standFor.speaker": "Pies de altavoz",
    "gearList.standFor.sheet-music-other": "Atriles y otros",
    "gearList.drumKit": "Batería",
    "gearList.otherInstruments": "Otros instrumentos",
    "gearList.drumKitMics": "Micrófonos de la batería",
    "gearList.otherMics": "Otros micrófonos y DI",
    "publicViewer.gear.eachFor": "Cada uno de los {n} — para qué sirve",
    "gearList.bandBlock": "Lo trae la banda",
    "gearList.where.standalone": "Posición propia",
    "gearList.where.standaloneMany": "{count} posiciones propias",
    "gearList.where.acrossStage": "{count} lugares en el escenario",
    "gearList.alsoUnder": "también en {section}: {count}",
    "gearList.derivedFrom": "de la lista de entradas, can. {channels} · {source} — se cambia en la lista de entradas",
    "gearList.groupCount": "{lines} líneas · {items} uds.",
    "gearList.groupCountOne": "1 línea · {items} uds.",
    "gearList.unstated.pa": "No especificado en este rider.",
    "gearList.unstated.mixing": "No especificado en este rider — calculado a partir de sus listas de entradas y salidas.",
    "gearList.unstated.band": "La banda trae el suyo — figura al final.",
    "gearList.mixing.need": "Lista de entradas: {inputs}. Lista de salidas: {outputs}.",
    "gearList.mixing.inputs.one": "{n} entrada",
    "gearList.mixing.inputs.few": "{n} entradas",
    "gearList.mixing.inputs.many": "{n} entradas",
    "gearList.mixing.inputs.other": "{n} entradas",
    "gearList.mixing.outputs.one": "{n} salida",
    "gearList.mixing.outputs.few": "{n} salidas",
    "gearList.mixing.outputs.many": "{n} salidas",
    "gearList.mixing.outputs.other": "{n} salidas",
    "gearList.mixing.modelUnstated": "Modelo no especificado.",
    "gearList.mixing.modelUnstatedBare": "modelo no especificado",
    "gearList.mixing.requirement": "Sistema de mezcla con una capacidad de al menos {inputs} / {outputs}: {picks}.",
    "gearList.mixing.monitorRequirement": "Consola de monitores con una capacidad de al menos {inputs} / {outputs}: {picks}.",
    "gearList.mixing.pickBrand": "cualquier consola {brand}",
    "mixing.line.title": "Sistema de mezcla",
    "mixing.monitorDesk.title": "Consola de monitores",
    "mixing.monitorConsole.title": "Consola de monitores",
    "mixing.monitor.fromFoh": "Desde FOH",
    "mixing.monitor.separate": "Consola aparte",
    "mixing.need.short": "≥{inputs} ent / {outputs} sal",
    "mixing.notSpecified": "Modelo no especificado",
    "mixing.line.tooltip": "{title}: {picks} — clic para editar",
    "mixing.dialog.need": "Al menos {inputs} entradas / {outputs} salidas (según las listas)",
    "mixing.dialog.close": "Cerrar",
    "mixing.dialog.remove": "Quitar {name}",
    "mixing.dialog.done": "Hecho",
    "mixing.dialog.digital": "Digitales",
    "mixing.dialog.analogue": "Analógicas",
    "mixing.dialog.brandsHint": "Elige una marca. Vale cualquiera de sus consolas que dé el ancho del show, o ábrela para elegir consolas concretas.",
    "mixing.dialog.back": "‹ Marcas",
    "mixing.dialog.anyBrand": "Cualquier consola {brand}",
    "mixing.dialog.anyBrandMeta": "todas las que dan el ancho del show: {fit} de {total}",
    "mixing.tier.senior": "Alta gama",
    "mixing.tier.mid": "Gama media",
    "mixing.tier.entry": "Entrada",
    "mixing.desk.figures": "{inputs} ent / {outputs} sal",
    "mixing.desk.unknown": "?",
    "mixing.desk.notStated": "capacidad no indicada",
    "mixing.desk.tooSmall": "demasiado pequeña para este rider",
    "mixing.desk.discontinued": "descatalogada",
    "mixing.pick.brand": "Cualquier {brand}",
    "gearList.bySection": "Por sección",
    "gearList.byKit": "Por kit",
    "gearList.groupingLabel": "Agrupar",
    "gearList.collapseAll": "Contraer todo",
    "gearList.expandAll": "Expandir todo",
    "printRider.gear.orderSummary": "A suministrar: {lines} líneas, {items} uds. en {sections} secciones.",
    "printRider.gear.orderSummaryBand": "La banda trae otras {lines} líneas: figuran al final y no deben suministrarse.",
    "printRider.gear.youOffer": "Usted ofrece:",
    "printRider.headers.offerHint": "✓ como se pide, o equivalente",
    "printRider.offer.date": "Fecha",
    "printRider.offer.city": "Ciudad",
    "printRider.offer.venue": "Recinto",
    "printRider.offer.company": "Empresa de alquiler",
    "printRider.offer.answeredBy": "Responde",
    "printRider.offer.weAsk": "Solicitamos",
    "printRider.offer.youOffer": "Ustedes ofrecen",
    "printRider.gear.derivedFrom": "de la lista de entradas, can. {channels}",
    "printRider.gear.summaryLabel": "Resumen",
    "printRider.gear.summaryOr": "o:",
    "printRider.gear.inDetail": "En detalle",
    "printRider.gear.sum.wedgeMixes": "Mezclas de monitores de suelo: {count}",
    "printRider.gear.sum.iemMixes": "Mezclas in-ear: {count}",
    "printRider.gear.sum.otherOutputs": "Otras salidas: {count}",
    "printRider.gear.sum.microphones": "Micrófonos: {count}",
    "printRider.gear.sum.microphonesWireless": "Micrófonos: {count}, inalámbricos: {wireless}",
    "printRider.gear.sum.diBoxes": "Cajas DI: {count}",
    "printRider.gear.sum.drumKit": "Batería, piezas: {pieces}",
    "printRider.gear.sum.more": "y {count} más",
    "printRider.offer.heading": "Este rider se responde en el propio documento",
    "printRider.offer.howTitle": "Cómo responder.",
    "printRider.offer.howTick": "marcado — exactamente como se pide",
    "printRider.offer.howEq": "la línea — el equivalente que ofrecen",
    "printRider.offer.howSummary": "Cada sección de equipo empieza con un resumen: márquelo si toda la sección es como se pide, y responda línea por línea solo donde lo suyo difiera.",
    "printRider.offer.howNo": "Lo que no puedan suministrar: escriban «no» y por qué. Una razón es una respuesta; el silencio no.",
    "printRider.offer.stageAsk": "Escenario {width} × {length} m.",
    "printRider.offer.signatures": "Firmas",
    "printRider.offer.preparedBy": "Oferta preparada por (empresa de alquiler / sala)",
    "printRider.offer.acceptedBy": "Aceptado por el grupo",
    "printRider.offer.signDate": "Fecha",
    "printRider.offer.answerOk": "OK",
    "printRider.offer.answerSub": "Sustituto: {model}",
    "printRider.offer.answerBandCarried": "Lo trae el grupo",
    "printRider.offer.answerNa": "No disponible: {reason}",
    "printRider.offer.answerVenue": "Lo pone el recinto",
    "printRider.offer.answerVenueNote": "Lo pone el recinto: {note}",
    "printRider.offer.answeredHeading": "Oferta de {supplier} · {status} · respuestas v{round}",
    "printRider.footer.offerFor": "Oferta para: ______________________",
    "printRider.footer.page": "Página {page} de {pages}",
    "printRider.publicLinkMissing":
        'Enlace público: aún no publicado — usa el botón "Publicar rider" (barra superior) para obtener un enlace con código QR.',
    "printRider.publicLinkLabel": "Enlace público (escanea o visita) — solo lectura — v{version}",
    "printRider.qrUnavailable": "Código QR no disponible (URL demasiado larga) — utiliza el enlace superior.",
    "printRider.qrAriaLabel": "Código QR con enlace a la página pública de este rider",
    "printRider.untitledRider": "Rider sin título",
    "printRider.stagePlotUnavailable": "Plano de escenario no disponible.",
    "printRider.stagePlotPending": "Capturando el escenario…",
    "printRider.stagePlanPending": "Generando el plano…",
    "printRider.stagePlotAlt": "Plano de escenario",
    "printRider.sections.stagePlan": "Plano del escenario — vista superior",
    "printRider.stagePlanAlt": "Plano del escenario, vista superior",
    "printRider.stagePlanUnavailable": "Plano del escenario no disponible.",
    "printRider.contactLabel": "Contacto: {name}",
    "printRider.telLabel": "Tel: {phone}",
    "printRider.emailLabel": "Email: {email}",
    "printRider.bandLogoAlt": "Logo de la banda",
    "printRider.footerPage": "Página {page} de {pages}",
    "printRider.docTitle": "{heading} — rider",
    "printRider.metaBrand": "SoundCheckBox rider",
    "printRider.idLabel": "ID: {id}",
    "printRider.unsavedId": "(sin guardar)",
    "printRider.stageSize": "Escenario: {width} × {length} m",
    "printRider.sections.stagePlot": "Plano del escenario",
    "printRider.sections.riderNotes": "Notas del rider",
    "printRider.sections.inputList": "Lista de entradas (Input list)",
    "printRider.sections.outputList": "Lista de salidas (Output list)",
    "printRider.sections.gearList": "Lista de equipamiento (Gear list)",
    // --- print settings panel (components/print-options-dialog.ts, SCRUM-96) ---
    "printVariant.heading": "¿Qué quieres imprimir?",
  "printVariant.simpleName": "Simple View",
  "printVariant.simpleNote": "Una página horizontal: el plano del escenario con las listas de entradas y salidas debajo.",
  "printVariant.techName": "Tech stage plot",
  "printVariant.techNote": "Una página, plano en blanco y negro — para el equipo técnico",
  "printVariant.bothName": "Ambas hojas",
  "printVariant.bothNote": "Dos páginas: blanco y negro para el equipo, color para el artista",
  "printVariant.fullName": "Imprimir todo",
  "printVariant.fullNote": "El rider completo, cada sección en su propia hoja.",
  "print.fileNameSimple": "{title} — Simple View",
  "print.fileNameTech": "{title} — Tech Plot",
  "print.fileNameBoth": "{title} — Tech Plot + Simple View",
  "print.fileNameFull": "{title} — Full Details",
  "simpleView.captionSuffix": "Mira el plano interactivo del escenario y haz una oferta para este rider en soundcheckbox.com",
  "simpleView.labelTech": "Tech stage plot",
  "simpleView.labelColour": "Simple view",
  "simpleView.captionUnpublished": "Rider no publicado — {site}",
  "editor.documentTitle": "{title} — SoundCheckBox",
  "editor.documentTitleUntitled": "Rider sin título",
  "simpleView.plotUnavailable": "Plano del escenario no disponible",
  "simpleView.overflow": "Algunas líneas no cupieron en esta hoja.",
  "simpleView.textClipped": "El resto del texto del rider está en el rider impreso completo y en su página pública.",
  "printOptions.heading": "Ajustes de impresión",
    "printOptions.openTitle": "Ajustes de impresión…",
    "printOptions.hint": "Elige qué va en la página. Deja vacío el encabezado de una columna para conservar el estándar.",
    "printOptions.presetFull": "Rider completo",
    "printOptions.presetOnePage": "Una página (horizontal)",
    "printOptions.gearLegend": "Lista de equipamiento",
    "printOptions.gearByCategory": "Agrupar por categoría",
    "printOptions.gearByKit": "Agrupar por kit",
    "printOptions.pageLegend": "Página",
    "printOptions.pageBreaks": "Cada sección en su propia página",
    "printOptions.landscapePage": "Página horizontal",
    "printOptions.alwaysLandscape": "Siempre horizontal, página completa",
    "printOptions.onCover": "En la portada",
    "printOptions.coverInfo": "Información",
    "printOptions.coverEditContacts": "Editar",
    "printOptions.coverSaveContacts": "Guardar",
    "printOptions.coverBandLink": "Enlace de la banda",
    "printOptions.coverOff": "no se imprime",
    "printOptions.coverMissing": "Vacío: {fields}",
    "printOptions.coverMissingShort": "vacío: {count}",
    "printOptions.sectionsLegend": "Secciones",
    "printOptions.lookLegend": "Aspecto",
    "printOptions.coverLegend": "Portada",
    "printOptions.cover": "Imprimir portada",
    "printOptions.coverLogoSize": "Tamaño del logotipo",
    "printOptions.coverTextSize": "Tamaño del texto",
    "printOptions.coverLogoFile": "Logotipo",
    "printOptions.coverNoLogo": "sin subir",
    "printOptions.coverLogoUpload": "Subir",
    "printOptions.coverLogoReplace": "Reemplazar",
    "printOptions.coverLogoClear": "Quitar",
    "printOptions.coverLogoFromRider": "El logotipo vive en el rider — el mismo aparece en la página pública y en el bloque de contactos",
    "printOptions.coverCheckEmpty": "vacío",
    "printOptions.coverCheck.logo": "Logotipo",
    "printOptions.coverCheck.band": "Banda",
    "printOptions.coverCheck.rider": "Rider",
    "printOptions.coverCheck.contact": "Contactos",
    "printOptions.coverCheck.link": "Enlace de la banda",
    "printOptions.coverCheck.qr": "QR del enlace público",
    "printOptions.coverLogo": "Logotipo",
    "printOptions.coverText": "Texto",
    "printOptions.align.left": "Izquierda",
    "printOptions.align.center": "Centrado",
    "printOptions.align.right": "Derecha",
    "printOptions.previewTitle": "Vista previa",
    "printOptions.previewBuilding": "Generando la vista…",
    "printOptions.previewFailed": "Vista previa no disponible",
    "printOptions.style.plain": "Sencillo",
    "printOptions.style.classical": "Clásico",
    "printOptions.style.jazz": "Jazz",
    "printOptions.style.rock": "Rock",
    "printOptions.style.metal": "Metal",
    "printOptions.style.disco": "Disco",
    "printOptions.palette.mono": "Blanco y negro",
    "printOptions.palette.graphite": "Grafito",
    "printOptions.palette.blue": "Azul",
    "printOptions.palette.red": "Roja",
    "printOptions.palette.amber": "Ámbar",
    "printOptions.palette.violet": "Violeta",
    "printOptions.inputColumnsLegend": "Columnas de la lista de entradas",
    "printOptions.outputColumnsLegend": "Columnas de la lista de salidas",
    "printOptions.confirm": "Imprimir",
    "printOptions.savePdf": "Guardar PDF",
    "printOptions.savedPdf": "PDF guardado.",
    "printOptions.section.contact": "Banda y contactos",
    "printOptions.section.publicLink": "Enlace público y QR",
    "printOptions.section.powerSummary": "Potencia total",
    "printOptions.section.stage3d": "Stage 3D view (in color)",
    "printOptions.section.techTopView": "Stage plot tech top view",

    // --- wysiwyg toolbar (components/wysiwyg-editor.ts) --------------------------------------------------
    "wysiwyg.bold": "Negrita",
    "wysiwyg.italic": "Cursiva",
    "wysiwyg.underline": "Subrayado",
    "wysiwyg.bulletedList": "Lista con viñetas",
    "wysiwyg.numberedList": "Lista numerada",
    "wysiwyg.clearFormatting": "Borrar formato",
    "wysiwyg.boldLabel": "N",
    "wysiwyg.italicLabel": "K",
    "wysiwyg.underlineLabel": "S",
    "wysiwyg.bulletedListLabel": "• Lista",
    "wysiwyg.numberedListLabel": "1. Lista",
    "wysiwyg.clearFormattingLabel": "⌫ Limpiar",

    // --- main.ts fatal startup overlay --------------------------------------------------------------------
    "main.fatal.heading": "Error al iniciar SoundCheckBox",
    "main.fatal.hint": "Envía este texto al equipo de soporte — ayuda a identificar el problema exactamente.",
    "main.fatal.userAgentLabel": "Agente de usuario: {ua}",

    // --- editor tour & cheatsheet (components/editor-tour.ts) ---------------------------------------------
    "tour.buttonTitle": "Tour interactivo y ayuda (?)",
    "tour.buttonLabel": "Tour",
    "tour.stepBadge": "Paso {current} de {total}",
    "tour.closeAriaLabel": "Cerrar",
    "tour.prev": "← Atrás",
    "tour.next": "Siguiente →",
    "tour.finish": "Finalizar",
    "tour.cheatSheetButton": "Guía rápida",
    "tour.cheatSheetButtonTitle": "Ver guía completa de atajos y modificadores",
    "tour.cheatSheetTitle": "Atajos y modificadores del editor",
    "tour.startTourButton": "Iniciar tour",
    "tour.closeSheetButton": "Cerrar",

    "tour.steps.stageSize.title": "1. Cambie el tamaño del escenario",
    "tour.steps.stageSize.desc": "Haga doble clic en el suelo del escenario (en pantalla táctil, toque dos veces). Arrastre los tiradores de los bordes y las esquinas para fijar el ancho y el fondo del escenario; el tamaño en metros se muestra arriba a la izquierda. Pulse <b>Terminar</b> o <b>Enter</b>.",
    "tour.steps.addObject.title": "2. Añada músicos y equipo",
    "tour.steps.addObject.desc": "Elija una pestaña abajo (<b>members</b> para los músicos, <b>Drums &amp; Perc</b>, <b>mic &amp; DI</b>, <b>monitors</b>, <b>backline</b>…). Haga clic en un preset o arrástrelo al escenario, y luego arrástrelo a su sitio.",
    "tour.steps.addObject.iem": "Algunos músicos prefieren in-ears.",
    "tour.steps.addObject.outputs": "La <b>Output List</b> se recalcula automáticamente.",
    "tour.steps.members.title": "3. Los músicos y su equipo",
    "tour.steps.members.desc": "Un músico (<b>members</b>) es la base de un preset: sus instrumentos, micrófonos y DI vienen con él. A veces es más cómodo ver en el escenario solo el equipo: mantenga pulsado <b>Alt</b>, haga clic en el músico y pulse <b>Delete</b> — el equipo se queda.",
    "tour.steps.arrange.title": "4. Organice el escenario",
    "tour.steps.arrange.desc": "Arrastre los objetos a su sitio. <b>Rotar</b> muestra un anillo: arrástrelo para girar la selección. <b>Vincular objeto a otro</b> y luego un clic en una tarima o un soporte fija el objeto a ellos, y así se mueven juntos. <b>Toda la pila</b> / <b>Un objeto</b> decide qué toma un clic. Gestos: <b>Guía rápida</b>. La posición relativa del equipo y las estructuras del escenario a veces se ajusta mejor en <b>Vista superior</b>.",
    "tour.steps.channels.title": "5. Input List y Output List",
    "tour.steps.channels.desc": "La <b>Input List</b> y la <b>Output List</b> se forman automáticamente a partir de lo que hay en el escenario; solo escriba los nombres de los músicos en la columna <b>Fuente</b>. Arrastre una fila para cambiar su <b>Ch</b>. Dé una dirección a cada mezcla: escriba en <b>Fuente</b> para quién está puesto cada monitor.",
    "tour.steps.micDi.title": "6. Micrófonos y DI",
    "tour.steps.micDi.desc": "Los micrófonos y las DI vienen con los presets. Haga clic en la celda <b>Micro / DI</b> de una fila y elija en el desplegable los que prefiera.",
    "tour.steps.planSheet.title": "7. Revise el Plano",
    "tour.steps.planSheet.desc": "Cambie a <b>Plano</b> y revise cómo queda su rider como dibujo: así lo verá el equipo técnico en el escenario. Aquí también se pueden mover los objetos.",
    "tour.steps.riderFill.title": "8. Rellene el rider y guárdelo",
    "tour.steps.riderFill.desc": "En la pestaña <b>Rider</b> de la derecha: escriba el <b>Texto del rider</b> y las notas necesarias, ponga el <b>Título</b> del rider y el <b>Nombre de la banda</b>, añada el <b>Logo de la banda</b>. Luego pulse arriba <b>Guardar Rider en la base de datos</b>.",
    "tour.steps.publishPrint.title": "9. Publicar e imprimir",
    "tour.steps.publishPrint.desc": "Con el rider guardado, <b>Publicar rider</b> crea un enlace de solo lectura para la sala y lo copia. <b>PDF</b> pregunta <b>¿Qué quieres imprimir?</b>: <b>Simple View</b>, <b>Tech stage plot</b> o <b>Ambas hojas</b>; <b>Imprimir todo</b> abre los <b>Ajustes de impresión</b> con vista previa.",
    "tour.end.title": "¿Limpiar el escenario o continuar?",
    "tour.end.desc": "El tour colocó equipo en el escenario. ¿Limpiarlo o seguir trabajando con este escenario?",
    "tour.end.clean": "Limpiar el escenario",
    "tour.end.keep": "Continuar con este escenario",
    "tour.banner.text": "¿Primera vez aquí? Haga un recorrido corto: el programa muestra cada paso en el escenario.",
    "tour.banner.start": "Iniciar el tour",
    "tour.banner.close": "Cerrar",

    "tour.cs.sectionNav": "Navegación 3D",
    "tour.cs.dragOrbit": "Rotar órbita de cámara",
    "tour.cs.dragPan": "Desplazamiento en plano",
    "tour.cs.zoom": "Acercar / alejar",
    "tour.cs.topView": "Alternar Vista Superior / 3D",

    "tour.cs.sectionModifiers": "Gestos y modificadores",
    "tour.cs.clickDefault": "Seleccionar y mover grupo o tarima",
    "tour.cs.altClick": "Seleccionar objeto individual dentro de grupo",
    "tour.cs.altDrag": "Mover objeto individual sin mover el grupo",
    "tour.cs.shiftGlue": "Pegar objeto arrastrado al objetivo",
    "tour.cs.altShiftDetach": "Despegar objeto del grupo en el lugar",
    "tour.cs.rotateGizmo": "Girar grupo (Alt para girar objeto individual)",

    "tour.cs.sectionObjectOps": "Operaciones de objeto",
    "tour.cs.opProperties": "Abrir parámetros de objeto y canales",
    "tour.cs.opRotate": "Activar anillo de rotación interactivo",
    "tour.cs.opAttach": "Vincular objeto seleccionado a otro",
    "tour.cs.opSavePreset": "Guardar grupo/objeto como preset de usuario",
    "tour.cs.opDelete": "Eliminar solo este objeto (hijos se conservan)",
    "tour.cs.opDeleteTree": "Eliminar objeto y todos sus hijos",

    "tour.cs.sectionHotkeys": "Atajos de teclado",
    "tour.cs.keyProperties": "Mostrar / ocultar las propiedades del objeto",
    "tour.cs.keyRotate": "Activar / desactivar el anillo de rotación",
    "tour.cs.keyInputList": "Mostrar / ocultar Input List",
    "tour.cs.keyOutputList": "Mostrar / ocultar Output List",
    "tour.cs.keyUndo": "Deshacer acción",
    "tour.cs.keyRedo": "Rehacer acción",
    "tour.cs.keySave": "Guardar rider",
    "tour.cs.keyEscape": "Deseleccionar / Cerrar diálogo",
    "sideNav.shows": "Shows",
    "shows.pageTitle": "Shows",
    "shows.tab.inbox": "Bandeja de entrada",
    "shows.tab.calendar": "Calendario",
    "shows.tabsLabel": "Secciones de shows",
    "shows.tab.list": "Shows",
    "shows.list.label": "Todos los shows",
    "shows.list.col.date": "Fecha",
    "shows.list.col.band": "Banda",
    "shows.list.col.venue": "Sala",
    "shows.list.col.city": "Ciudad",
    "shows.list.col.rental": "Alquiler",
    "shows.list.col.status": "Estado",
    "shows.list.empty": "Aún no hay shows.",
    "shows.list.emptyFiltered": "No hay shows en esta selección.",
    "shows.list.rentalOne": "1 oferta",
    "shows.list.rentalMany": "{n} ofertas",
    "shows.list.rentalNone": "Aún no hay ofertas",
    "shows.list.rentalYou": "Tú",
    "shows.list.dateOpen": "Fecha aún sin fijar",
    "shows.list.state.sketch": "Borrador",
    "shows.mcal.label": "Calendario de shows",
    "shows.mcal.prev": "Mes anterior",
    "shows.mcal.next": "Mes siguiente",
    "shows.mcal.showAll": "Mostrar todos",
    "shows.mcal.filterDay": "Shows del {day}",
    "shows.mcal.filterMonth": "Shows de {month}",
    "shows.mcal.today": "hoy",
    "shows.loadFailed": "No se pudieron cargar tus shows: {error}",
    "shows.inbox.empty": "Ahora mismo nada te necesita.",
    "shows.inbox.listLabel": "Lo que te necesita",
    "shows.inbox.unknownWho": "Un proveedor",
    "shows.inbox.approvalNew": "{who} envió una oferta para {subject} ({when}). Espera tu aprobación.",
    "shows.inbox.approvalAgain": "{who} volvió a enviar la oferta para {subject} ({when}). Espera tu aprobación.",
    "shows.inbox.newOffer": "{who} envió una oferta para {subject} ({when}).",
    "shows.inbox.reconfirm": "El rider cambió (v{from} a v{to}). Vuelve a confirmar tu oferta para {subject} ({when}).",
    "shows.inbox.declined": "Tu oferta para {subject} ({when}) fue rechazada.",
    "shows.inbox.declinedReason": "Motivo: {reason}",
    "shows.inbox.riderRequest": "Se pidió un rider para {subject} ({when}).",
    "shows.inbox.open": "Abrir",
    "shows.inbox.dateOpen": "fecha aún sin fijar",
    "shows.inbox.received": "Recibido {at}",
    "shows.inbox.new": "Nuevo",
    "sideNav.unreadCount": "{n} sin leer",
    "shows.cal.month": "Mes",
    "shows.cal.week": "Semana",
    "shows.cal.list": "Lista",
    "shows.cal.today": "Hoy",
    "shows.cal.prev": "Anterior",
    "shows.cal.next": "Siguiente",
    "shows.cal.noEvents": "No hay shows en este período",
    "shows.cal.waiting": "Esperando una fecha",
    "shows.cal.waitingHint": "Los shows sin fecha aún no están en la cuadrícula.",
    "shows.cal.waitingEmpty": "Nada espera una fecha.",
    "shows.cal.sketch": "Borrador",
    "shows.cal.untitled": "Show sin título",
    "shows.feed.title": "Suscríbete en tu calendario",
    "shows.feed.hint": "Google, Apple y Outlook aceptan esta dirección mediante «suscribirse por URL».",
    "shows.feed.hidden": "Tu enlace está activo. Por seguridad solo se muestra al crearlo: renuévalo para obtener uno nuevo.",
    "shows.feed.addressLabel": "Tu dirección privada de calendario",
    "shows.feed.create": "Crear enlace",
    "shows.feed.rotate": "Renovar",
    "shows.feed.rotateConfirm": "Haz clic de nuevo: el enlace anterior dejará de funcionar",
    "shows.feed.turnOff": "Desactivar",
    "shows.feed.turnOffConfirm": "Haz clic de nuevo para desactivar",
    "shows.feed.failed": "No se pudo actualizar el enlace del calendario: {error}",
    "shows.detail.back": "Shows",
    "shows.detail.loadFailed": "No se pudo leer este show: {error}",
    "shows.detail.state.open": "Abierto",
    "shows.detail.state.waiting": "Esperando ofertas",
    "shows.detail.state.offers_in": "Ofertas recibidas",
    "shows.detail.state.agreed": "Acordado",
    "shows.detail.state.past": "Pasado",
    "shows.detail.acts": "Actuaciones",
    "shows.detail.noActs": "Aún no hay ningún artista en este show.",
    "shows.detail.riderVersion": "Rider v{version}",
    "shows.detail.noRider": "Aún no hay rider",
    "shows.detail.newerVersion": "Hay una versión más reciente: v{version}",
    "shows.detail.getLink": "Mostrar el enlace del show",
    "shows.detail.linkFailed": "No se pudo obtener el enlace: {error}",
    "shows.detail.sketchTitle": "Borrador — aún sin fecha ni sala",
    "shows.detail.sketchHint": "Indica una fecha o una sala y pasará a ser un show normal.",
    "shows.detail.sketchWhere": "Fecha y sala",
    "shows.detail.change": "Cambiar fecha y lugar",
    "shows.detail.changeNeedsDate": "Elige una fecha: el show ya tiene una.",
    "shows.detail.sketchSave": "Guardar fecha y sala",
    "shows.detail.sketchNeeds": "Elige primero una fecha o una sala.",
    "shows.detail.saveFailed": "No se pudo guardar: {error}",
    "shows.offers.heading": "Ofertas",
    "shows.offers.empty": "Aún no hay ofertas.",
    "shows.offers.sortLabel": "Ordenar por",
    "shows.offers.sort.time": "Hora (enviada)",
    "shows.offers.sort.match": "Coincidencia",
    "shows.offers.sort.fewestMissing": "Menos faltantes",
    "shows.offers.sort.price": "Precio",
    "shows.offers.sort.priceNotYet": "aún no",
    "shows.offers.match": "{n} coinciden",
    "shows.offers.missing": "{n} faltan",
    "shows.offers.riderChanged": "Rider cambiado v{from} → v{to}",
    "shows.offers.open": "Abrir oferta",
    "shows.offers.organiserApprove": "Aprobar como organizador",
    "shows.offers.organiserRequestChanges": "Solicitar cambios como organizador",
    "shows.offers.actionFailed": "No se pudo hacer: {error}",
    // --- crew: the hired worker's screen and the admin's test link (SCRUM-762) ---
    "crew.untitledShow": "Show",
    "crew.dateOpen": "Fecha sin definir",
    "crew.venueNone": "Recinto sin definir",
    "crew.signInWhy": "Este es un trabajo en un show. Inicia sesión o crea una cuenta para aceptarlo: lleva un minuto.",
    "crew.signInToTake": "Inicia sesión para aceptar este trabajo",
    "crew.take": "Aceptar este trabajo",
    "crew.takenBySomeone": "Otra persona ya aceptó este trabajo. Pide a quien te envió el enlace uno para ti.",
    "crew.gone": "Este enlace de trabajo no existe o fue retirado.",
    "crew.failed": "Algo salió mal: {error}",
    "crew.saveFailed": "No se guardó la marca: {error}",
    "crew.noRider": "Este show aún no tiene un rider publicado, así que no hay nada que marcar.",
    "crew.riderGone": "El rider de este show ya no está publicado, así que las listas están vacías. Los pasos siguen aquí.",
    "crew.riderMoved": "El rider se actualizó después de acordar este show: estas listas son las nuevas.",
    "crew.riderMovedOn": "Rider actualizado el {date}: más nuevo que lo acordado para este show. Estas listas son las nuevas.",
    "crew.tab.out": "Almacén",
    "crew.tab.venue": "Recinto",
    "crew.tab.return": "Vuelta",
    "crew.tab.plan": "Plan",
    "crew.time.warehouse": "Almacén",
    "crew.time.truck": "Camión",
    "crew.time.venue": "Recinto",
    "crew.time.showRun": "Show",
    "crew.time.strike": "Desmontaje",
    "crew.thisPart": "Esta parte",
    "crew.doneOf": "{done} de {total}",
    "crew.done": "Hecho",
    "crew.twoPeople": "Cargar entre dos",
    "crew.showRunTwo": "2 personas",
    "crew.kit.inside": "Qué lleva",
    "crew.kit.cablesApart": "Los cables se cuentan en el bloque Cables",
    "crew.kit.included": "en la caja",
    "crew.kit.consumablesApart": "Las pilas y los consumibles se cuentan en su propia línea",
    "crew.truckFront": "FRENTE DEL CAMIÓN",
    "crew.missing": "Falta",
    "crew.broken": "Roto",
    "crew.missingOpen": "FALTA: sigue abierto",
    "crew.brokenOpen": "ROTO: sigue abierto",
    "crew.less": "Uno menos",
    "crew.more": "Uno más",
    "crew.tickAria": "{name}: {done} de {total}",
    "crew.hint.fromOffer": "No está en este rider: viene de la oferta del alquiler",
    "crew.hint.nothing": "Aquí no hay nada",
    "crew.returnOpen": "{n} líneas siguen abiertas (sin volver, faltan o rotas). Quedan en la lista del alquiler: no desaparece nada.",
    "crew.returnAllBack": "Todo ha vuelto.",
    // SCRUM-760: «Loading done — depart» gate. Translated like the neighbouring crew.* keys.
    "crew.gate.depart": "Carga terminada — salir",
    "crew.gate.departVenue": "Cargado — salir del recinto",
    "crew.gate.confirm": "¿El camión sale ya? Pulsa otra vez",
    "crew.gate.waiting": "Esperando a que el conductor dé la salida",
    "crew.gate.departed": "Salió {time} · {name}",
    "crew.gate.departedNoName": "Salió {time}",
    "crew.gate.by.driver": "Enviado por el conductor",
    "crew.gate.by.supplier": "Enviado por la empresa de alquiler",
    "crew.gate.locked": "El camión ya salió: la carga está cerrada. Pide al conductor que la reabra si se olvidó algo.",
    "crew.gate.next": "Seguir — {tab}",
    "crew.gate.reopen": "Reabrir la carga",
    "crew.gate.failed": "No se pudo actualizar la salida: {error}",
    "crew.zoom": "Zoom",
    "crew.fit": "Ajustar",
    "crew.legendDone": "hecho {n}",
    "crew.legendPart": "parcial {n}",
    "crew.legendTodo": "pendiente {n}",
    "crew.legendMiss": "falta / roto {n}",
    "crew.planHint": "Las marcas se ponen por bloques; el plan las muestra por capas, en el orden del día.",
    "crew.changeover.title": "Cambios entre actuaciones",
    "crew.changeover.heading": "Cambio {from} → {to}",
    "crew.changeover.headingMin": "Cambio {from} → {to} ({n} min)",
    "crew.changeover.none": "Nada que mover: lo que necesita la siguiente actuación ya está en su sitio.",
    "crew.changeover.recheck": "Después, prueba de líneas, canales: {channels}",
    "crew.zone.USR": "Fondo derecha",
    "crew.zone.USC": "Fondo centro",
    "crew.zone.USL": "Fondo izquierda",
    "crew.zone.SR": "Derecha",
    "crew.zone.CS": "Centro del escenario",
    "crew.zone.SL": "Izquierda",
    "crew.zone.DSR": "Frente derecha",
    "crew.zone.DSC": "Frente centro",
    "crew.zone.DSL": "Frente izquierda",
    "crew.step.toWingsStand": "Llevar {item} con su pie a bambalinas, sin desconectar el cable",
    "crew.step.rollOut": "Sacar {item} rodando a bambalinas",
    "crew.step.back": "Traer {item} de vuelta desde bambalinas",
    "crew.step.swapStand": "Quitar {from} y poner {to} en el mismo pie",
    "crew.step.replace": "Cambiar {from} por {to}",
    "crew.step.add": "Montar {item}",
    "crew.step.bandOut": "Retirar del escenario el equipo de {act}: {items}",
    "crew.step.bandIn": "Montar el equipo de {act}: {items}",
    "crew.layer.picked": "Preparado",
    "crew.layer.loaded": "Cargado",
    "crew.layer.placed": "Colocado",
    "crew.layer.patched": "Conectado",
    "crew.layer.checked": "Comprobado",
    "crew.layer.unpatched": "Desconectado",
    "crew.layer.struck": "Desmontado",
    "crew.layer.loadedback": "Cargado de vuelta",
    "crew.layer.returned": "Devuelto",
    "crew.phase.pick": "Preparar en el almacén",
    "crew.phase.pickSub": "Orden de descarga: el camión se carga al revés",
    "crew.phase.load": "Cargar el camión",
    "crew.phase.loadSub": "Orden inverso · lo pesado al frente del camión",
    "crew.phase.setup": "Descargar y montar",
    "crew.phase.setupSub": "En el recinto",
    "crew.phase.showRun": "Show",
    "crew.phase.showRunSub": "El equipo sigue de guardia: las tareas de escenario vendrán después",
    "crew.phase.strike": "Desmontaje y carga",
    "crew.phase.strikeSub": "Después del show",
    "crew.phase.back": "De vuelta en el almacén",
    "crew.phase.backSub": "Descargar y registrar: qué volvió, qué falta, qué está roto",
    "crew.block.carts": "Carros y rampa",
    "crew.block.racks": "Racks y alimentación",
    "crew.block.pa": "P.A.",
    "crew.block.mon": "Monitores",
    "crew.block.foh": "FOH",
    "crew.block.mics": "Micrófonos",
    "crew.block.dis": "DIs",
    "crew.block.stands": "Pies y soportes",
    "crew.block.backline": "Backline",
    "crew.block.risers": "Tarimas y mobiliario",
    "crew.block.cables": "Cables",
    "crew.block.other": "Otros",
    "crew.block.loadOut": "Carga · {block}",
    "crew.block.unloadToPositions": "Descargar a posiciones",
    "crew.block.unloadToPositionsSub": "Lo pesado directo a su sitio, el FOH a su posición",
    "crew.block.paMonitorsInPlace": "P.A. y monitores en su sitio",
    "crew.block.powerPa": "Alimentación del P.A.",
    "crew.block.powerStage": "Alimentación del escenario",
    "crew.block.powerFoh": "Alimentación del FOH",
    "crew.block.paCheck": "Prueba del P.A. y front fills",
    "crew.block.stageSet": "Escenario según rider",
    "crew.block.stageSetSub": "En paralelo con la prueba del P.A.",
    "crew.block.patch": "Conexionado",
    "crew.block.patchSub": "Cada entrada de la lista de entradas",
    "crew.block.lineCheck": "Line check",
    "crew.block.lineCheckSub": "Llega el técnico de la banda y empieza la prueba de sonido",
    "crew.block.showRun": "Show",
    "crew.block.showRunSub": "En el escenario durante el show",
    "crew.block.showRunN": "Show {n}",
    "crew.banner.powerOn": "Encendido: primero las fuentes (consola, stage rack, inalámbricos), luego los amplificadores.",
    "crew.banner.powerOff": "Apagado: primero los amplificadores, luego las fuentes (consola, stage rack, inalámbricos).",
    "crew.block.unpatch": "Desconexionado",
    "crew.block.clearSmall": "Despejar escenario · pequeño, ligero y cables",
    "crew.block.clearSmallSub": "Primero micros, DI, pies y cables",
    "crew.block.clearLarge": "Despejar escenario · grande",
    "crew.block.clearLargeSub": "Amplis, tarimas, piano, monitores",
    "crew.block.fohPaLast": "FOH y P.A.",
    "crew.block.fohPaLastSub": "Lo último en salir, lo primero en entrar al montaje",
    "admin.crew.title": "Enlace de equipo para ti (pruebas)",
    "admin.crew.hint": "Te añade como personal en uno de tus shows para probar la pantalla del trabajador desde el móvil. Abre el enlace en el móvil y acepta el trabajo.",
    "admin.crew.show": "Show",
    "admin.crew.make": "Crear enlace de equipo",
    "admin.crew.made": "Enlace creado. Ábrelo en tu móvil.",
    "admin.crew.link": "Enlace de equipo",
    "admin.crew.open": "Abrir",
    "admin.crew.noShows": "Aún no tienes shows: crea uno primero en Shows",
    "admin.crew.dateOpen": "sin fecha",
    "admin.crew.failed": "No se pudo hacer: {error}",
    "admin.perf.title": "Añadir una actuación a un show",
    "admin.perf.hint": "Lo convierte en un show de varias actuaciones: la pestaña «Recinto» del enlace de equipo mostrará los cambios entre actuaciones. Usa el show elegido arriba.",
    "admin.perf.name": "Nombre de la actuación",
    "admin.perf.rider": "Rider (publicado)",
    "admin.perf.noRider": "Aún sin rider",
    "admin.perf.add": "Añadir actuación",
    "admin.perf.added": "Añadida. El show tiene ahora {n} actuaciones.",
    "admin.perf.failed": "No se pudo añadir: {error}",
    "admin.perf.needName": "Pon un nombre a la actuación",

    // --- rental desk (rental/desk-answer-panel.ts, rental/desk-shows-panel.ts, pages/warehouse-tabs.ts, SCRUM-647) ---
    "rental.tabsLabel": "Secciones del almacén",
    "rental.loadFailed": "No se pudo cargar: {error}",
    "rental.toAnswer.heading": "Por responder",
    "rental.toAnswer.empty": "Nada espera por ti: todas las ofertas tienen respuesta.",
    "rental.cover.positions": "{n} posiciones",
    "rental.cover.positionsOne": "{n} posición",
    "rental.cover.ok": "{n} de tu almacén",
    "rental.cover.short": "{n} insuficientes",
    "rental.cover.missing": "{n} faltan",
    "rental.cover.answered": "respondidas {n}",
    "rental.cover.emptyWarehouse": "Tu almacén está vacío: añade equipo para ver la cobertura",
    "rental.reason.draft": "Aún no enviada",
    "rental.reason.changes": "Piden cambios",
    "rental.reason.reconfirm": "El rider cambió: vuelve a confirmar",
    "rental.shows.heading": "Mis shows",
    "rental.shows.empty": "Aún no hay shows. Un show aparece aquí cuando envías una oferta para una fecha.",
    "rental.shows.dateOpen": "Fecha sin fijar",
    "rental.shows.venueNone": "Lugar sin fijar",
    "rental.shows.myOffer": "Mi oferta: {status}",
    "rental.crew.make": "Enlace para el equipo",
    "rental.crew.making": "Creando…",
    "rental.crew.newLink": "Nuevo enlace para el equipo: envíalo a un técnico",
    "rental.crew.linksHeading": "Enlaces para el equipo",
    "rental.crew.free": "Nadie lo ha tomado aún",
    "rental.crew.takenBy": "Tomado por {name}",
    "rental.crew.taken": "Tomado",
    "rental.crew.failed": "No se pudo: {error}",
    "rental.crew.withdraw": "Retirar",
    "rental.crew.withdrawSure": "¿Retirar? Pulsa otra vez",
    "rental.crew.withdrawFailed": "No se pudo retirar el enlace: {error}",
    // SCRUM-760: driver link, role on a link row, the departure line. Translated like the neighbouring rental.crew.* keys.
    "rental.crew.driver": "Enlace para el conductor",
    "rental.crew.role.crew": "Equipo",
    "rental.crew.role.driver": "Conductor",
    "rental.crew.takeMyself": "Tomarlo yo",
    "rental.crew.takenByYou": "Tomado por ti",
    "rental.crew.openMine": "Abrir",
    "rental.depart.out": "Salió del almacén",
    "rental.depart.venue": "Salió del recinto",
    "rental.depart.reopen": "Reabrir la carga",
    "rental.depart.failed": "No se pudo actualizar la salida: {error}",
    "rental.progress.heading": "Avance del trabajo",
    "rental.tasks.heading": "Qué hace el equipo",
    "rental.tasks.blocks": "{n} bloques",
    "rental.tasks.template": "Nuestro orden de trabajo",
    "rental.tasks.templateNote": "Los mismos pasos en cada show. Un show propio les añade las cantidades: qué cargar, parchear y comprobar según el rider.",
    "rental.progress.doneOf": "{done} de {total}",
    "rental.progress.noLinks": "Aún no hay enlace para el equipo: el avance aparece cuando un técnico marca algo.",
    "rental.progress.noRider": "Este show no tiene rider publicado: solo cuentan los pasos.",
    "rental.progress.failed": "El avance no se pudo cargar: {error}",
};